/* ═══════════════════════════════════════════════════════
   NYC Suite — Data Context
   ═══════════════════════════════════════════════════════
   Manages: all business data + CRUD operations + loading states
   Components use: useData() hook instead of receiving 15+ props
   
   Per-collection loading states let panels render independently.
*/

import { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from "react";
import { dataService } from "../lib/dataService.js";
import { getImages, saveImages } from "../lib/imageStorage.js";
import {
  validateJobFields, BLANK_JOB, saveClientCodeBeforeBooking,
  BLANK_CONTACT, BLANK_CLIENT, BLANK_CONVERSATION, BLANK_TASK,
  mkClientCode, mkJobId, addBizDays, localDayISO,
  isHydratedSessionId, migrateJob, migrateQuote,
  sanitizeParentClientId,
} from "../lib/utils.js";
import {
  KV_KEY_GCAL_INTEGRATION, KV_KEY_CREW,
  SESSION_LOCATION_FIELDS, SESSION_SCHEDULE_FIELDS,
  CREW_ROSTER, STORAGE_OPTIONS, TRANSPORT_OPTIONS,
} from "../lib/constants.js";
import { resolveQuoteRevenue, quoteContactBlocker, isActiveQuoteRecord, LINK_CONTACT_MESSAGE } from "../lib/builderHelpers.js";
import { isProBono, withProBonoTag } from "../lib/proBono.js";
import { getEqNames } from "../lib/equipment.js";
import { rosterId } from "../lib/holds.js";
import { planContactCommits, stripFailedClientRefs } from "../lib/contactCommit.js";
import { useCollectionState } from "./useCollectionState.js";
import { useQuoteWorkspace } from "./useQuoteWorkspace.js";
import UnsavedQuoteDialog from "../components/UnsavedQuoteDialog.jsx";
import { deleteAttachmentFile } from "../lib/attachmentStorage.js";
import { CREW_ROLES_DEFAULT, stripServerOwned, SERVER_OWNED_CARD_FIELDS, CAPTURE_SUBJECTS } from "../lib/crewCardRules.js";
import notify from "../lib/notify.jsx";
import { callCalendar } from "../lib/calendarClient.js";
import { syncJobSessions, deleteJobEvents, needsCalendarReconcile, reconcilePrevJob, calendarNeedsSync } from "../lib/calendarSync.js";
import { isHHmm } from "../lib/calendarEvents.js";
import { jobAccess } from "../apps/jobs/lib/jobAccess.js";

const DataContext = createContext(null);

// A Firestore Timestamp, a Date, or an ISO string to an ISO string ("" when
// unreadable). Hold records carry a server timestamp; every other updatedAt in
// the app is an ISO string, so the context hands out the same shape.
const toISO = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v;
  try {
    const d = typeof v.toDate === "function" ? v.toDate() : v;
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
    return d.toISOString();
  } catch {
    return "";
  }
};

// holds/{quoteId} documents as useData() exposes them (holds slice 2 contract):
// { id: quoteId, label, clientName, subject, contactName, location, isStudio,
//   dates: [{ date, dayType, kinds }], equipmentIds, crew: [{ id, name }],
//   fingerprint, updatedAt (ISO string) }.
// The identity fields (2026-09-05) are passed through only when the record has
// them: a record written before then keeps no clientName key, which is how
// lib/holds.js holdRecordEntries knows to fall back to the quotes join.
const normalizeHold = (raw) => ({
  id: raw.quoteId || raw._docId,
  label: raw.label || "",
  ...(raw.clientName === undefined ? {} : {
    clientName: raw.clientName || "",
    subject: raw.subject || "",
    contactName: raw.contactName || "",
    location: raw.location || "",
    isStudio: !!raw.isStudio,
  }),
  dates: Array.isArray(raw.dates) ? raw.dates : [],
  equipmentIds: Array.isArray(raw.equipmentIds) ? raw.equipmentIds : [],
  crew: Array.isArray(raw.crew) ? raw.crew : [],
  fingerprint: raw.fingerprint || "",
  updatedAt: toISO(raw.updatedAt),
});

const jobHasRevenueSyncGuard = (job) => !!(job?.invoiced || job?.paid || job?.invoiceDate || job?.paidDate);

async function refreshLinkedJob(localJob, quoteId) {
  const localId = localJob?.id || localJob?._docId;
  if (!localId) return localJob;
  const freshJob = await dataService.jobs.get(localId);
  if (!freshJob) return localJob;
  const normalized = { ...freshJob, id: freshJob.id || freshJob._docId || localId };
  if (normalized.deletedAt || normalized.quoteId !== quoteId) return null;
  return normalized;
}

// Emergency fallback — used only when Firestore equipment_config AND pp-equipment KV are both unavailable.
// The real source of truth is Firestore app/equipment_config.
const FALLBACK_EQUIPMENT = {
  "Scanners": [
    { id: "big_rig",   name: "Big Rig",                     note: "Full photogrammetry rig" },
    { id: "head_rig",  name: "Head Rig",                    note: "Head/face capture rig" },
    { id: "leo",       name: "Artec Leo",                   note: "Handheld structured light" },
    { id: "spider",    name: "Artec Space Spider II",       note: "High-res small objects" },
    { id: "spider_i",  name: "Artec Space Spider I",        note: "High-res small objects (Gen 1)" },
    { id: "portal",    name: "XGrids PortalCam",            note: "Gaussian splatting camera" },
    { id: "nimble",    name: "Scanology NimbleTrack Gen 2", note: "Blue laser scanner" },
  ],
  "Cameras & Sensors": [
    { id: "r5",        name: "Canon R5",                    note: "Reference photography" },
    { id: "rtc",       name: "Leica RTC 360",               note: "Large-scale environment" },
    { id: "drone",     name: "DJI Mavic 3E",                note: "Aerial photogrammetry" },
  ],
  "Support Gear": [
    { id: "flying_fox", name: "Flying Fox",                 note: "Overhead cable cam" },
  ],
};

// Hydrate legacy string-array rosters into {name, email, phone, receivesAllUpdates}.
// Empty-name objects preserved — users may clear a name mid-edit; dropping them would silently lose email/phone/flag data.
function normalizeCrewRoster(roster) {
  if (!Array.isArray(roster)) return [];
  return roster.reduce((acc, entry) => {
    if (typeof entry === "string") {
      if (entry) acc.push({ name: entry, email: "", phone: "", receivesAllUpdates: false });
    } else if (entry && typeof entry === "object") {
      acc.push({
        name: entry.name || "",
        email: entry.email || "",
        phone: entry.phone || "",
        receivesAllUpdates: !!entry.receivesAllUpdates,
      });
    }
    return acc;
  }, []);
}

// True when applying `updates` to `current` would produce an identical job shape.
// Skips arming the debounced save (→ migrateJob → syncJobToCalendar) on keystrokes
// that re-enter the same value. `sessions` is compared one level deep; other fields
// use Object.is (new arrays/objects from upstream still count as changes, matching
// React equality semantics callers already expect).
function isJobPatchNoOp(current, updates) {
  for (const [k, v] of Object.entries(updates)) {
    if (k === "sessions") {
      const prev = current.sessions || [];
      if (!Array.isArray(v) || v.length !== prev.length) return false;
      for (let i = 0; i < v.length; i++) {
        const ns = v[i];
        const ps = prev[i];
        if (ns === ps) continue;
        if (!ns || !ps) return false;
        const nsKeys = Object.keys(ns);
        if (nsKeys.length !== Object.keys(ps).length) return false;
        for (const sk of nsKeys) if (!Object.is(ns[sk], ps[sk])) return false;
      }
      continue;
    }
    if (!Object.is(current[k], v)) return false;
  }
  return true;
}

/* ═══ Card writes (crew + client cards, docs/crew-cards/SHEETS-V2-SLICE-2-SPEC.md 6.1 + 4.1) ═══ */
// What a card write may carry: never the server-owned fields, never the
// local-only markers (the sections add _seeded to a card built from the
// seeds; the loaders add _docId; the crewCards listener's documents carry
// _pending).
const cardWritePayload = (card) => {
  const out = stripServerOwned(card);
  delete out._docId;
  delete out._seeded;
  delete out._pending;
  return out;
};
// Migration stored a record's id as its doc key, not as a field.
const ensureIdField = (doc) => doc.id ? doc : { ...doc, id: doc._docId };

const pickKeys = (obj, keys) => {
  const out = {};
  keys.forEach(k => { if (k in obj) out[k] = obj[k]; });
  return out;
};
// Same card field: primitives by identity, arrays and objects by content —
// the sections hand over the whole card on every edit, so a rebuilt array
// with the same rows is not a change. updatedAt is stamped on every save
// and never counts.
const sameCardValue = (a, b) => Object.is(a, b)
  || (!!a && !!b && typeof a === "object" && typeof b === "object" && JSON.stringify(a) === JSON.stringify(b));
export const changedCardKeys = (local, record) =>
  Object.keys(record).filter(k => k !== "updatedAt" && !sameCardValue(local?.[k], record[k]));
// A card's crew call: "HH:mm" or "" (anything malformed reads as none).
const crewCallOf = (card) => (isHHmm(card?.crewCall) ? card.crewCall : "");
// Crew calls by session id for one job, from a card list; empty ones absent.
export const crewCallsOf = (cards, jobDocId) => {
  const out = {};
  (cards || []).forEach(c => {
    if (!c || c.jobId !== jobDocId) return;
    const t = crewCallOf(c);
    if (t) out[c.sessionId] = t;
  });
  return out;
};
// A crew card's subject as the calendar wording reads it (startLineLabel):
// one of CAPTURE_SUBJECTS, else "" (unknown, which reads as people).
const crewSubjectOf = (card) => {
  const s = typeof card?.subject === "string" ? card.subject.trim() : "";
  return CAPTURE_SUBJECTS.includes(s) ? s : "";
};
// Crew card subjects by session id for one job, from a card list; unknown absent.
export const crewSubjectsOf = (cards, jobDocId) => {
  const out = {};
  (cards || []).forEach(c => {
    if (!c || c.jobId !== jobDocId) return;
    const s = crewSubjectOf(c);
    if (s) out[c.sessionId] = s;
  });
  return out;
};

/* ═══ The calendar's committed view of the crew cards ═══
   {[cardId]: {jobId, sessionId, crewCall, subject}}: the crew call and the
   subject of every crew card AS THE SERVER HOLDS IT, never as this tab is
   editing it. Fed by the crewCards listener (the server's data, before
   mergeCardSnapshot's local-edit protection) and by a card write that landed
   (the keys it carried). Every calendar entry point reads it
   (committedCalendarFor: syncJobToCalendar, the backfill, the crew-card
   sync), so a job save, a reconcile, the backfill or another card's write
   never publishes a pending edit; the edit's own write publishes it once,
   when it lands. It is what the calendar SHOULD have. What the calendar HAS
   is not kept here: it is each session's gcalSyncedFingerprint, written back
   after a successful calendar call and shared through the jobs listener
   (calendarSync.syncedCalendarStateOf). */
const committedCardCalendarEntry = (card) => ({ jobId: card.jobId, sessionId: card.sessionId, crewCall: crewCallOf(card), subject: crewSubjectOf(card) });
// The store from a crewCards snapshot as the listener receives it: documents
// as stored, keyed by the document id when one has no id field of its own
// (as mergeCardSnapshot keys them).
export const committedCardCalendarOf = (cards) => {
  const out = {};
  (cards || []).forEach(raw => {
    if (!raw) return;
    const c = raw.id ? raw : { ...raw, id: raw._docId };
    if (c.id) out[c.id] = committedCardCalendarEntry(c);
  });
  return out;
};
// The store after a crewCards snapshot, document by document: a document the
// server holds (`_pending` false) sets its entry from the server's data; one
// with this client's write still unacknowledged (`_pending` true) keeps the
// entry it has (the write's own landing sets it); a card gone from the
// snapshot leaves the store.
export const committedCardCalendarAfterSnapshot = (store, docs) => {
  const next = {};
  (docs || []).forEach(raw => {
    if (!raw) return;
    const c = raw.id ? raw : { ...raw, id: raw._docId };
    if (!c.id) return;
    if (raw._pending) { if (store[c.id]) next[c.id] = store[c.id]; return; }
    next[c.id] = committedCardCalendarEntry(c);
  });
  return next;
};
// The store after a crew-card write landed: the entry takes the keys the
// write carried (`sent`: the whole record, or exactly the patch), so a patch
// that did not carry the crew call or the subject leaves them as the listener
// last delivered them. The card's local copy is not the source: it may hold a
// later edit still pending, or a value another tab has since replaced.
export const withCommittedCard = (store, card, sent, whole) => {
  const was = store[card.id] || { crewCall: "", subject: "" };
  return {
    ...store,
    [card.id]: {
      jobId: card.jobId, sessionId: card.sessionId,
      crewCall: whole || "crewCall" in sent ? crewCallOf(sent) : was.crewCall,
      subject: whole || "subject" in sent ? crewSubjectOf(sent) : was.subject,
    },
  };
};
// One job's committed crew calls and subjects by session id.
const committedCalendarMaps = (store, jobDocId) => {
  const cards = Object.values(store || {});
  return { crewCalls: crewCallsOf(cards, jobDocId), crewSubjects: crewSubjectsOf(cards, jobDocId) };
};

/* ═══ The persisted sync state, read when a calendar op runs ═══
   `job` with the event id and synced fingerprint `latest` holds for each of
   its sessions (by session id): `latest` is the job as this tab holds it when
   an op runs at the head of the job's serial calendar queue, so a fingerprint
   written back by the op ahead of it, or delivered by the jobs listener from
   another tab's sync, is the baseline the op diffs against, never the copy
   captured when the op was queued. Everything else about `job` (the schedule
   before or after the edit being synced) is kept. */
export const withPersistedCalendarState = (job, latest) => {
  if (!job || !Array.isArray(job.sessions) || !job.sessions.length || !latest) return job;
  // Sessions by id; one without an id never matches another (no lookup under undefined).
  const byId = new Map((latest.sessions || []).filter(s => s?.id).map(s => [s.id, s]));
  let changed = false;
  const sessions = job.sessions.map(s => {
    const l = byId.get(s.id);
    if (!l || (s.gcalEventId === l.gcalEventId && s.gcalSyncedFingerprint === l.gcalSyncedFingerprint)) return s;
    changed = true;
    return { ...s, gcalEventId: l.gcalEventId, gcalSyncedFingerprint: l.gcalSyncedFingerprint };
  });
  return changed ? { ...job, sessions } : job;
};

/* ═══ The sync state of a snapshot this tab cannot take whole ═══
   `remote` is the job as the jobs listener delivered it while a job save is
   pending here, or this tab's own write is not yet acknowledged: the local
   copy is ahead of it and stays, except for the sessions' sync fields, which
   another tab's successful sync wrote back and are the truth: each session's
   event id and synced fingerprint, by session id (withPersistedCalendarState),
   and a single-day job's root pair, which its hydrated session is read from
   (migrateJob). Nothing else of `local` moves; the same object back when
   nothing differs. */
export const withRemoteSyncState = (local, remote) => {
  if (!local || !remote) return local;
  let next = withPersistedCalendarState(local, remote);
  if (!local.multiDay) {
    const eventId = remote.gcalEventId || null;
    const fingerprint = remote.gcalSyncedFingerprint || null;
    if ((local.gcalEventId || null) !== eventId || (local.gcalSyncedFingerprint || null) !== fingerprint) {
      next = { ...next, gcalEventId: eventId, gcalSyncedFingerprint: fingerprint };
    }
  }
  return next;
};

/* A change made elsewhere to a card's crew call or subject queues the job's
   calendar op this long after the card arrives (applyCrewCardsSnapshot): the
   editing tab's fingerprint write-back follows its calendar request, which
   takes 1 to 3 seconds, so by the time the op checks it has normally arrived
   and nothing is sent. */
export const REMOTE_CARD_SYNC_GRACE_MS = 10000;

// `role` is the signed-in user's role (users/{uid}.role); it normalizes through
// jobAccess, so an unknown or blank role reads as viewer, and the prop defaults
// to viewer so a caller that forgets it fails closed (JobsApp always passes it,
// keyed on the role so a change remounts the provider). Admins load and
// subscribe to everything, as before. Non-admin roles (crew, viewer) never
// request quotes, contacts, clients, docs, assessments, campaigns,
// aiConversations, tasks, crewCards, or clientCards; jobs, holds, and the
// studio settings (pp-* KV plus equipment_config) load for every role, and
// every quote write path is a no-op for them (holds slice 2).
export function DataProvider({ children, role = "viewer" }) {
  const access = jobAccess(role);
  const isAdmin = access.isAdmin;
  const normalizedRole = access.role;

  // ═══ Collection states with per-collection loading ═══
  const quotes = useCollectionState([]);
  const jobs = useCollectionState([]);
  // The jobs as this tab holds them, readable outside a render, and the list
  // every write to the jobs state starts from. setJobsData runs an updater
  // here, once, at the call, on the ref (never inside React's own state
  // updater: React runs that at the call only while nothing is queued on the
  // hook, and defers it to the next render otherwise), moves the ref, then
  // hands React the computed list. So the ref is authoritative and
  // synchronous: a calendar op reads the job as it is when the op RUNS
  // (syncJobToCalendar), a write-back that landed a moment ago included, and
  // a render never moves the ref back to an older list. Every write to the
  // jobs state goes through setJobsData (the load, the jobs listener, a job
  // save, the calendar write-back).
  const jobsRef = useRef(jobs.data);
  const setJobsData = useCallback((v) => {
    const next = typeof v === "function" ? v(jobsRef.current) : v;
    jobsRef.current = next;
    jobs.setData(next);
  }, []);
  // Holds (docs/holds/holds-plan.md): the crew-readable projection of every open
  // quote's dates, written only by the holdsProjection triggers. Read-only here.
  const holds = useCollectionState([]);
  const contacts = useCollectionState([]);
  const clients = useCollectionState([]);
  const assessments = useCollectionState([]);
  const campaigns = useCollectionState([]);
  /* CRM v3 slice 4 — saved audiences. Rules only, no membership: every count
     the Segments tab shows is computed live from contacts/clients/jobs/quotes
     by src/lib/segments.js. Admin-only, alongside contacts and clients. */
  const segments = useCollectionState([]);
  const aiConversations = useCollectionState([]);
  const tasks = useCollectionState([]);
  // Crew Cards: card-only fields per job session (docs/crew-cards/DESIGN.md).
  const crewCards = useCollectionState([]);
  const crewCardsRef = useRef(crewCards.data);
  // What the calendar may read of the crew cards (see committedCardCalendarOf
  // above). Starts from the initial cards: none in production, where the
  // listener seeds it; the loaded cards in the test harness.
  const committedCardCalendarRef = useRef(null);
  if (committedCardCalendarRef.current === null) committedCardCalendarRef.current = committedCardCalendarOf(crewCards.data);
  const clientCards = useCollectionState([]);
  const clientCardsRef = useRef(clientCards.data);
  const crewCardSaveTimers = useRef({});
  const crewCardWriteChains = useRef({});
  // Per card (collection/id), what the write path learned across debounce
  // windows: `stored` (a whole-record write from this tab landed) and
  // `pendingKeys` (the keys of a failed patch, owed to the next write).
  const cardWriteMemo = useRef({});
  // One job's maps by session id from the COMMITTED store (never a card as
  // this tab is editing it): `crewCalls`, each landed card's crew call
  // ("HH:mm", validated; absent when empty or malformed), where the crew
  // calendar event starts (SHEETS-V2-SLICE-2-SPEC 4.1); `crewSubjects`
  // ("people" | "objects" | "places"; unknown absent), the wording of the
  // calendar line under a moved start ("Talent arrives" / "Start").
  const committedCalendarFor = useCallback((jobDocId) => committedCalendarMaps(committedCardCalendarRef.current, jobDocId), []);
  // Queues the job's calendar sync after a crew-card write landed carrying a
  // calendar key; assigned below syncJobToCalendar (saveCard is declared
  // before the queue helpers and holds only the ref).
  const afterCrewCallWrite = useRef(null);
  // Grace timers for remote card changes, one per job (jobId -> timer); see
  // applyCrewCardsSnapshot. Cleared on unmount.
  const remoteCardSyncTimers = useRef(new Map());
  // Set once the first crewCards snapshot has been taken: after that an empty
  // store is an empty collection, not page load, and a card added elsewhere is
  // a remote change like any other.
  const committedStoreHydrated = useRef(false);
  // Jobs whose remote card change was held back because this tab's pending
  // write on that card had touched a calendar key (applyCrewCardsSnapshot):
  // the grace check is scheduled when that write settles without queuing a
  // sync of its own (saveCard), so the repair is never lost. Cleared on unmount.
  const deferredRemoteRepairs = useRef(new Set());
  // One grace timer per job: the job's calendar check REMOTE_CARD_SYNC_GRACE_MS
  // from now, unless one is pending already (a later change inside the window
  // gets the remaining grace). Used by applyCrewCardsSnapshot and saveCard.
  const scheduleRemoteCardSync = useCallback((jobId) => {
    if (!jobId || remoteCardSyncTimers.current.has(jobId)) return;
    remoteCardSyncTimers.current.set(jobId, setTimeout(() => {
      remoteCardSyncTimers.current.delete(jobId);
      afterCrewCallWrite.current?.(jobId);
    }, REMOTE_CARD_SYNC_GRACE_MS));
  }, []);

  // ═══ Builder state (current quote) ═══
  // Lives in useQuoteWorkspace (called below, once the callbacks it needs exist). This ref
  // lets the initial load reach it for the recovery copy. The quotes list is mirrored in a
  // ref so the saving code reads the latest list right after it writes one.
  const workspaceRef = useRef(null);
  const quotesLive = useRef([]);
  // Every write to the quotes list goes through here: the mirror first, synchronously,
  // then React state. The listener's snapshots included, so a save completing between a
  // snapshot and the render can't replace the newer list with an older one.
  const setQuotesData = useCallback((valueOrUpdater) => {
    quotesLive.current = typeof valueOrUpdater === "function" ? valueOrUpdater(quotesLive.current) : valueOrUpdater;
    quotes.setData(quotesLive.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ═══ Billing state ═══
  const [billingQuoteId, setBillingQuoteId] = useState("");
  const [billingNotes, setBillingNotes] = useState("");

  // ═══ Quote list filters ═══
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterClient, setFilterClient] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // ═══ CRM import ═══
  const [crmImportStatus, setCrmImportStatus] = useState(null);

  // Track whether initial load is complete
  const [loaded, setLoaded] = useState(false);
  // Debounced job saves, one timer PER job (a second job edited inside the
  // 800 ms window no longer drops the first job's save). Each entry carries a
  // deferred that resolves true when the write reached Firestore and false
  // when it was cancelled or failed; the last outcome per job is remembered so
  // Crew Card Deploy can refuse to run against a job whose save failed.
  const jobSaveTimers = useRef({});
  const jobSaveOutcome = useRef({});
  // Writes to one job land in the order they were scheduled, so a slow earlier
  // save can never land after (and clobber) a newer one.
  const jobWriteChains = useRef({});
  const settleJobSave = (entry, ok) => {
    if (jobSaveTimers.current[entry.jobId] === entry) delete jobSaveTimers.current[entry.jobId];
    jobSaveOutcome.current[entry.jobId] = ok;
    entry.resolve(ok);
  };
  // Cancels a save still in its debounce window. A write already in flight
  // cannot be cancelled: the returned promise resolves once every queued write
  // for the job has settled, so delete/unbook wait for it instead of racing it.
  const cancelPendingJobSave = (jobId) => {
    const e = jobSaveTimers.current[jobId];
    if (e && !e.fired) { clearTimeout(e.timer); settleJobSave(e, false); }
    return (jobWriteChains.current[jobId] || Promise.resolve()).then(() => undefined);
  };
  const anyJobSavePending = () => Object.keys(jobSaveTimers.current).length > 0;
  // Map of jobId → pre-edit snapshot (first edit in the 800ms debounce window).
  const prevJobRef = useRef(new Map());
  // Map of jobId → tail of a per-job calendar-op promise chain. EVERY calendar
  // mutation for a job (create/update sync AND delete cleanup) is enqueued here
  // and runs strictly in submission order, so book→unbook, edit→unbook, and
  // delete→restore on the same job can never interleave and orphan or duplicate
  // an event. This is the serialization patchJobRoot's comment assumes exists.
  const calendarOpQueue = useRef(new Map());
  // Avoids closure-capture staleness for calendar sync called from updateJob's setTimeout.
  const contactsRef = useRef([]);

  // ═══ Studio settings (equipment / crew / storage / transport) ═══
  const [studioSettings, setStudioSettings] = useState({
    equipment: FALLBACK_EQUIPMENT,
    crewRoster: normalizeCrewRoster(CREW_ROSTER),
    storageOpts: STORAGE_OPTIONS,
    transportOpts: TRANSPORT_OPTIONS,
    roleOpts: CREW_ROLES_DEFAULT,
  });
  const ppTimers = useRef({ equipment: null, crewRoster: null, storageOpts: null, transportOpts: null, roleOpts: null });
  // Per-field hydration flags. Set on hydration (success OR failure), consumed by each per-field auto-save effect to skip its first run.
  // Per-field so that editing one field doesn't suppress saves for others.
  const studioHydrated = useRef({ equipment: false, crewRoster: false, storageOpts: false, transportOpts: false, roleOpts: false });
  // Per-field write chains. Serialize writes to each KV doc so a slower in-flight write can't resolve after a newer one and clobber it.
  const ppWriteChains = useRef({ equipment: Promise.resolve(), crewRoster: Promise.resolve(), storageOpts: Promise.resolve(), transportOpts: Promise.resolve(), roleOpts: Promise.resolve() });
  // Holds (2026-09-04): the live Settings roster (KV pp-crew) with a derived id per
  // entry (lib/holds.js rosterId: the email, else a name slug). The one list the
  // quote's Crew picker and the conflict check read; CREW_ROSTER in constants.js
  // is only the load fallback seeded above.
  const crewRoster = useMemo(() => (studioSettings.crewRoster || []).map(entry => ({
    id: rosterId(entry),
    name: entry?.name || "",
    email: entry?.email || "",
    phone: entry?.phone || "",
    receivesAllUpdates: !!entry?.receivesAllUpdates,
  })).filter(entry => entry.id), [studioSettings.crewRoster]);
  // The live equipment id-to-name map (lib/equipment.js), re-read whenever the
  // studio equipment settings change so every list and builder memo keyed on it
  // recomputes; the settings are read here only to key the memo.
  const equipmentSettings = studioSettings.equipment;
  const equipmentMap = useMemo(() => {
    const map = getEqNames();
    if (import.meta.env?.DEV && equipmentSettings) {
      for (const list of Object.values(equipmentSettings)) {
        for (const item of list || []) {
          if (item?.id && !map[item.id]) console.warn(`Equipment "${item.id}" is in the studio settings but not in the loaded name map.`);
        }
      }
    }
    return map;
  }, [equipmentSettings]);


  /* ═══ INITIAL LOAD — parallel with per-collection error handling ═══ */
  useEffect(() => {
    let mounted = true;
    (async () => {
      // Load all data in parallel
      try {
        const [result, eqConfig] = await Promise.all([
          dataService.loadAll({ admin: isAdmin }),
          dataService.kv.getRaw("equipment_config"),
        ]);
        if (!mounted) return;

        // Collections
        setQuotesData(result.quotes.map(migrateQuote));
        quotes.setLoading(false);

        // The recovery copy (the autosave of unsaved edits; lib/quotePersistence.js
        // installRecoveredCopy decides whether it still applies). It is lean (no image
        // previews, lib/draftRecord.js); previews live in the local IndexedDB cache, so
        // rehydrate them here the same way loadQuote does for saved quotes.
        // Non-admin loads carry no copy (loadAll skips it).
        const copyQuote = result.draft?.v === 2 ? result.draft.quote : result.draft;
        // The recovery key could not be read: the autosave stays off until it can be,
        // so an unseen copy is never written over (lib/quotePersistence.js recoveryUnknown).
        if (isAdmin && result.draftReadFailed) {
          workspaceRef.current?.persistence.recoveryUnknown({
            contacts: (result.contacts || []).map(ensureIdField),
            clients: (result.clients || []).map(ensureIdField),
          });
        }
        if (isAdmin && copyQuote?.id) {
          // Installed now, before anything is awaited: the list is published, and a
          // quote opened from it while this waited would make the copy unseen (and its
          // autosave would write over it). The previews come after, into the installed
          // quote (updateQuote: dropped if another quote is open by then; a preview is
          // not content, so the quote is as clean or as dirty as it was).
          // With the contacts and clients as loaded: they reach the persistence's getters
          // only on the next render, and the copy's baseline clears a dead link with them.
          const persistence = workspaceRef.current?.persistence;
          const installed = persistence?.installRecoveredCopy(result.draft, {
            contacts: (result.contacts || []).map(ensureIdField),
            clients: (result.clients || []).map(ensureIdField),
          });
          // Not awaited: the load goes on (and `loaded` turns true, so the recovery
          // autosave runs for edits made meanwhile); the previews land when the cache
          // answers, through updateQuote, which drops them if another quote is open.
          if (installed && Array.isArray(copyQuote.attachments) && copyQuote.attachments.some(a => a?.type === "image")) {
            void (async () => {
              try {
                const imgMap = await getImages(copyQuote.id);
                if (!mounted || !imgMap) return;
                persistence.updateQuote(copyQuote.id, q => ({
                  ...q,
                  attachments: (q.attachments || []).map(a => (a?.type === "image" && imgMap[a.id] && !a.dataUrl ? { ...a, dataUrl: imgMap[a.id] } : a)),
                }));
              } catch (e) { console.warn("Draft image cache load:", e); }
            })();
          }
        }
        
        // Old job docs can lack the id data-field (doc key only) — without it the
        // Jobs-list row click opens nothing (same backfill contacts/clients get).
        setJobsData(result.jobs.map(migrateJob).map(j => j.id ? j : { ...j, id: j._docId }));
        jobs.setLoading(false);
        
        // Ensure id field exists (migration stored id as doc key, not as field)
        const ensureId = ensureIdField;
        // Merge, don't replace: a contact added (Add & link) while the first load
        // was still running exists only locally until the listener starts, and
        // replacing would drop it and with it the quote's new link.
        const loadedContacts = (result.contacts || []).map(ensureId);
        contacts.setData(prev => {
          const ids = new Set(loadedContacts.map(c => c.id));
          return [...loadedContacts, ...prev.filter(c => c && c.id && !ids.has(c.id))];
        });
        contacts.setLoading(false);

        clients.setData((result.clients || []).map(ensureId));
        clients.setLoading(false);

        assessments.setData(result.assessments || []);
        assessments.setLoading(false);

        campaigns.setData(result.campaigns || []);
        campaigns.setLoading(false);

        segments.setData((result.segments || []).map(ensureId));
        segments.setLoading(false);

        aiConversations.setData(result.aiConversations || []);
        aiConversations.setLoading(false);

        tasks.setData(result.tasks || []);
        tasks.setLoading(false);

        holds.setData((result.holds || []).map(normalizeHold));
        holds.setLoading(false);

        // Studio settings: Firestore equipment_config wins, fall back to pp-equipment KV (skipping stale eq* IDs), then inline FALLBACK.
        const pp = result.studioSettings || {};
        let resolvedEquipment = null;
        if (eqConfig?.equipment?.length) {
          const grouped = {};
          for (const item of eqConfig.equipment) {
            if (!item.active) continue;
            const cat = item.category || "Other";
            if (!grouped[cat]) grouped[cat] = [];
            grouped[cat].push({ id: item.id, name: item.name, note: item.note });
          }
          resolvedEquipment = grouped;
        } else if (pp.equipment) {
          const hasOldIds = Object.values(pp.equipment).flat().some(
            item => item?.id && /^eq\d/.test(item.id)
          );
          if (!hasOldIds) resolvedEquipment = pp.equipment;
        }
        setStudioSettings(prev => ({
          equipment: resolvedEquipment || prev.equipment,
          crewRoster: pp.crewRoster ? normalizeCrewRoster(pp.crewRoster) : prev.crewRoster,
          storageOpts: pp.storageOpts || prev.storageOpts,
          transportOpts: pp.transportOpts || prev.transportOpts,
          roleOpts: Array.isArray(pp.roleOpts) && pp.roleOpts.length ? pp.roleOpts : prev.roleOpts,
        }));
        studioHydrated.current = { equipment: true, crewRoster: true, storageOpts: true, transportOpts: true, roleOpts: true };

        setLoaded(true);
      } catch (e) {
        console.error("DataProvider load error:", e);
        // Mark all as loaded (with errors) so UI isn't stuck
        [quotes, jobs, contacts, clients, assessments, campaigns, segments, aiConversations, tasks, holds].forEach(c => {
          c.setError(e.message);
          c.setLoading(false);
        });
        // Arm hydration flags on failure too, so the auto-save effects don't fire and overwrite real Firestore data with FALLBACK defaults.
        studioHydrated.current = { equipment: true, crewRoster: true, storageOpts: true, transportOpts: true, roleOpts: true };
        setLoaded(true);
      }
    })();
    return () => { mounted = false; };
  }, []);

  /* ═══ REAL-TIME LISTENERS (activate after initial load) ═══
     Skip snapshots from our own pending writes (hasPendingWrites: true)
     since we already have the data locally. Only accept confirmed server
     data, which includes our completed writes AND other users' changes.
     Jobs also skip during debounce window (write hasn't been sent yet). */
  // A snapshot this tab cannot take whole (below) still carries the truth
  // about the calendar: only the sessions' sync fields move
  // (withRemoteSyncState), and a job save still in its debounce window takes
  // them too, so the whole-job write that follows carries the fingerprint the
  // calendar has, not the copy it was queued with (that copy would put the
  // old fingerprint back, and the next sync would send the change again).
  const mergeRemoteSyncState = (incoming) => {
    const byId = new Map(incoming.map(j => [j.id, j]));
    setJobsData(prev => {
      let changed = false;
      const next = prev.map(local => {
        const remote = byId.get(local.id);
        if (!remote) return local;
        const merged = withRemoteSyncState(local, remote);
        // The pending save's payload takes them too, whether or not the list
        // changed: the listener echo of this tab's own write-back finds the
        // list already updated while the payload still holds the older
        // fingerprint. (The write overlays them again when it executes.)
        const pending = jobSaveTimers.current[local.id];
        if (pending && !pending.fired) pending.job = withRemoteSyncState(pending.job, merged);
        if (merged === local) return local;
        changed = true;
        return merged;
      });
      return changed ? next : prev;
    });
  };
  // The jobs listener: the server's jobs, migrated. A snapshot with this
  // client's pending write, or one arriving inside a job save's debounce
  // window, is not taken whole (the local copy is ahead of it): only the
  // sessions' sync fields are merged from it. Also on the context, so a test
  // can deliver a snapshot (effects never run in the renderToString harness):
  // a fingerprint another tab's sync wrote back arrives this way.
  const applyJobsSnapshot = (data, meta) => {
    const incoming = data.map(migrateJob).map(j => j.id ? j : { ...j, id: j._docId });
    if (meta.hasPendingWrites || anyJobSavePending()) { mergeRemoteSyncState(incoming); return; }
    setJobsData(incoming);
  };
  useEffect(() => {
    if (!loaded) return;
    // Every role: jobs and holds. Admins only: the business collections below.
    const unsubs = [
      dataService.jobs.subscribe(applyJobsSnapshot),
      dataService.holds.subscribe((data, meta) => {
        if (meta.hasPendingWrites) return;
        holds.setData(data.map(normalizeHold));
      }),
    ];
    if (!isAdmin) return () => unsubs.forEach(fn => fn());
    unsubs.push(
      dataService.quotes.subscribe((data, meta) => {
        if (meta.hasPendingWrites) return;
        const records = data.map(migrateQuote);
        setQuotesData(records);
        // The other computer's list action on the quote open here (Mark lost, a
        // follow-up) reaches the builder as a list action would.
        workspaceRef.current?.persistence?.followListSnapshot(records);
      }),
      dataService.contacts.subscribe((data, meta) => {
        if (meta.hasPendingWrites) return;
        const ensureId = (doc) => doc.id ? doc : { ...doc, id: doc._docId };
        contacts.setData(data.map(ensureId));
      }),
      dataService.clients.subscribe((data, meta) => {
        if (meta.hasPendingWrites) return;
        const ensureId = (doc) => doc.id ? doc : { ...doc, id: doc._docId };
        clients.setData(data.map(ensureId));
      }),
      dataService.assessments.subscribe((data, meta) => {
        if (meta.hasPendingWrites) return;
        assessments.setData(data);
      }),
      dataService.segments.subscribe((data, meta) => {
        if (meta.hasPendingWrites) return;
        const ensureId = (doc) => doc.id ? doc : { ...doc, id: doc._docId };
        segments.setData(data.map(ensureId));
      }),
      /* Campaigns are written by three parties, and only one of them is this
         browser: the /crm chat writes drafts, and the campaignSend callable
         and the nightly results sweep write statuses, send ids and results.
         Load-once meant a chat-written draft and last night's numbers only
         appeared after a full page reload. Same admin-only, role-keyed shape
         as segments. */
      dataService.campaigns.subscribe((data, meta) => {
        if (meta.hasPendingWrites) return;
        const ensureId = (doc) => doc.id ? doc : { ...doc, id: doc._docId };
        campaigns.setData(data.map(ensureId));
      }),
      dataService.tasks.subscribe((data, meta) => {
        if (meta.hasPendingWrites) return;
        tasks.setData(data);
      }),
      dataService.aiConversations.subscribe((data, meta) => {
        if (meta.hasPendingWrites) return;
        aiConversations.setData(data);
      }),
      dataService.crewCards.subscribe(applyCrewCardsSnapshot, { metadata: true }),
      dataService.clientCards.subscribe((data, meta) => {
        if (meta.hasPendingWrites) return;
        clientCardsRef.current = mergeCardSnapshot("clientCards", data, clientCardsRef.current);
        clientCards.setData(clientCardsRef.current);
      }),
    );
    return () => unsubs.forEach(fn => fn());
  }, [loaded, isAdmin]);

  useEffect(() => { contactsRef.current = contacts.data; }, [contacts.data]);

  /* ═══ Studio settings auto-save (debounced, per field) ═══
     Split into four per-field effects so editing one setting doesn't trigger
     writes for the other three (which can overwrite concurrent edits from
     nyc-suite during the shared-Firestore window with stale in-memory state).
     Each effect skips its first post-hydration run via its own flag. */
  useEffect(() => {
    if (!loaded) return;
    if (studioHydrated.current.equipment) { studioHydrated.current.equipment = false; return; }
    if (ppTimers.current.equipment) clearTimeout(ppTimers.current.equipment);
    ppTimers.current.equipment = setTimeout(() => {
      ppWriteChains.current.equipment = ppWriteChains.current.equipment.then(async () => {
        try { await dataService.kv.set("pp-equipment", studioSettings.equipment); }
        catch (e) { console.error("Failed to save equipment settings", e); notify.error("Failed to save equipment settings. Please try again."); }
      });
    }, 600);
    return () => clearTimeout(ppTimers.current.equipment);
  }, [studioSettings.equipment, loaded]);

  useEffect(() => {
    if (!loaded) return;
    if (studioHydrated.current.crewRoster) { studioHydrated.current.crewRoster = false; return; }
    if (ppTimers.current.crewRoster) clearTimeout(ppTimers.current.crewRoster);
    ppTimers.current.crewRoster = setTimeout(() => {
      ppWriteChains.current.crewRoster = ppWriteChains.current.crewRoster.then(async () => {
        try { await dataService.kv.set(KV_KEY_CREW, studioSettings.crewRoster); }
        catch (e) { console.error("Failed to save crew roster", e); notify.error("Failed to save crew roster. Please try again."); }
      });
    }, 600);
    return () => clearTimeout(ppTimers.current.crewRoster);
  }, [studioSettings.crewRoster, loaded]);

  useEffect(() => {
    if (!loaded) return;
    if (studioHydrated.current.storageOpts) { studioHydrated.current.storageOpts = false; return; }
    if (ppTimers.current.storageOpts) clearTimeout(ppTimers.current.storageOpts);
    ppTimers.current.storageOpts = setTimeout(() => {
      ppWriteChains.current.storageOpts = ppWriteChains.current.storageOpts.then(async () => {
        try { await dataService.kv.set("pp-storage", studioSettings.storageOpts); }
        catch (e) { console.error("Failed to save storage options", e); notify.error("Failed to save storage options. Please try again."); }
      });
    }, 600);
    return () => clearTimeout(ppTimers.current.storageOpts);
  }, [studioSettings.storageOpts, loaded]);

  useEffect(() => {
    if (!loaded) return;
    if (studioHydrated.current.transportOpts) { studioHydrated.current.transportOpts = false; return; }
    if (ppTimers.current.transportOpts) clearTimeout(ppTimers.current.transportOpts);
    ppTimers.current.transportOpts = setTimeout(() => {
      ppWriteChains.current.transportOpts = ppWriteChains.current.transportOpts.then(async () => {
        try { await dataService.kv.set("pp-transport", studioSettings.transportOpts); }
        catch (e) { console.error("Failed to save transport options", e); notify.error("Failed to save transport options. Please try again."); }
      });
    }, 600);
    return () => clearTimeout(ppTimers.current.transportOpts);
  }, [studioSettings.transportOpts, loaded]);

  useEffect(() => {
    if (!loaded) return;
    if (studioHydrated.current.roleOpts) { studioHydrated.current.roleOpts = false; return; }
    if (ppTimers.current.roleOpts) clearTimeout(ppTimers.current.roleOpts);
    ppTimers.current.roleOpts = setTimeout(() => {
      ppWriteChains.current.roleOpts = ppWriteChains.current.roleOpts.then(async () => {
        try { await dataService.kv.set("pp-roles", studioSettings.roleOpts); }
        catch (e) { console.error("Failed to save crew roles", e); notify.error("Failed to save crew roles. Please try again."); }
      });
    }, 600);
    return () => clearTimeout(ppTimers.current.roleOpts);
  }, [studioSettings.roleOpts, loaded]);

  /* ═══ CREW CARDS ═══
     Card-only fields (assignments, checklist, scope, open items…) for one job
     session. Optimistic local update + a debounced setDoc(merge) per card id.
     Job fields never come through here — the Crew Card form writes those via
     updateJob, exactly like the Prep tab. Server-written fields
     (SERVER_OWNED_CARD_FIELDS: lastDeploy, recipientStatus, deployLock,
     deployInFlight) are never written from here: every save strips them. */
  // A card with a save still in its debounce window keeps its local edits;
  // only the server-owned fields are taken from the snapshot, so a deploy
  // result or another admin's write cannot revert typing.
  const mergeCardSnapshot = (collectionName, data, localList) => data.map(raw => {
    const c = raw.id ? raw : { ...raw, id: raw._docId };
    if (!crewCardSaveTimers.current[`${collectionName}/${c.id}`]) return c;
    const local = (localList || []).find(x => x.id === c.id);
    if (!local) return c;
    // Every server-owned field comes from the snapshot, including ones the
    // server removed (a finished deploy clears deployInFlight).
    const merged = { ...local };
    SERVER_OWNED_CARD_FIELDS.forEach(f => { if (f in c) merged[f] = c[f]; else delete merged[f]; });
    return merged;
  });
  // The crewCards listener, subscribed with metadata: a document with this
  // client's unacknowledged write arrives marked `_pending`, and the
  // acknowledgement itself is delivered. The committed store is updated
  // document by document, before the whole-snapshot gate below, so a change
  // made elsewhere reaches it while this tab has a write of its own pending
  // on another card (the gate used to drop that snapshot whole, and the next
  // job save moved the other tab's event back). A change made elsewhere to a
  // card's crew call or subject (a document the server holds whose entry
  // differs from the stored one, or is new with either set) queues the job's
  // calendar op REMOTE_CARD_SYNC_GRACE_MS after the card arrives, with one
  // pending timer per job (a second remote change for the job inside the
  // window adds none). A card this tab has a calendar change pending on (a
  // write whose window touched the crew call or the subject) holds the check
  // back until that write settles: its landing queues the op when a calendar
  // key travels (an acknowledgement that outruns the write is this tab's
  // change), else the grace check is scheduled then (deferredRemoteRepairs);
  // an unrelated pending patch suppresses nothing. Whether the
  // calendar already has the change is decided
  // when the op runs (syncJobToCalendar): nothing when the session's
  // fingerprint records it (the other tab synced it, and its write-back
  // reached this tab through the jobs listener, normally well inside the
  // grace: its calendar request takes 1 to 3 seconds); the change once when
  // that sync failed or never ran there; and the repair when this tab moved
  // the event back against the stale card (the fingerprint arrived before
  // the card and a job save here ran in between). The grace timer is
  // anchored to the first remote change (a later change inside the window
  // gets the remaining grace), and there is no cross-tab lock: a duplicate
  // notification is possible when two tabs publish the same job within the
  // same seconds; the calendar converges on the next sync. The first
  // snapshot, into an empty store (never seeded: page
  // load), hydrates it and queues nothing: page load must never notify
  // anyone, and a sync that failed elsewhere is retried by the next edit or
  // job save here, as before. A card gone from the snapshot leaves the
  // store and queues nothing: nothing in the app deletes a card, so the
  // calendar was not told, and the next sync moves its event back to the
  // session call. The card list keeps the whole-snapshot gate: it is replaced
  // only from a snapshot with no pending write, and `_pending` never enters
  // it. Also on the context, so a test can deliver a snapshot (effects never
  // run in the renderToString harness).
  const applyCrewCardsSnapshot = (data, meta) => {
    const before = committedCardCalendarRef.current || {};
    const after = committedCardCalendarAfterSnapshot(before, data);
    committedCardCalendarRef.current = after;
    const hydrating = !committedStoreHydrated.current && Object.keys(before).length === 0;
    committedStoreHydrated.current = true;
    const jobsToSync = new Set();
    if (!hydrating) (data || []).forEach(raw => {
      if (!raw || raw._pending) return;
      const id = raw.id || raw._docId;
      if (!id) return;
      const was = before[id];
      const now = after[id];
      if (!now?.jobId) return;
      const changed = was ? (was.crewCall !== now.crewCall || was.subject !== now.subject) : !!(now.crewCall || now.subject);
      if (!changed) return;
      // This tab's own calendar change still pending on the card (a write
      // whose window touched the crew call or the subject, owed keys
      // included, or a whole record): held back, never dropped. The write's
      // landing queues the op when a calendar key travels (an acknowledgement
      // that outruns the write is that change); when none does after all (a
      // revert inside the window keeps the key in the window, not in the
      // patch) or the write fails, saveCard schedules the grace check then
      // (deferredRemoteRepairs). An unrelated pending patch (the initials)
      // suppresses nothing: the remote change is checked as usual.
      const pending = crewCardSaveTimers.current[`crewCards/${id}`];
      const owed = cardWriteMemo.current[`crewCards/${id}`]?.pendingKeys;
      if (pending && (pending.whole || ["crewCall", "subject"].some(k => pending.keys?.has(k) || owed?.has(k)))) {
        deferredRemoteRepairs.current.add(now.jobId);
        return;
      }
      jobsToSync.add(now.jobId);
    });
    jobsToSync.forEach(jobId => scheduleRemoteCardSync(jobId));
    if (meta.hasPendingWrites) return;
    const docs = data.map(d => { const c = { ...d }; delete c._pending; return c; });
    crewCardsRef.current = mergeCardSnapshot("crewCards", docs, crewCardsRef.current);
    crewCards.setData(crewCardsRef.current);
  };

  // Returns a promise that resolves once the debounced write has reached
  // Firestore (or failed), so Deploy can await it instead of guessing.
  // Shared by crew and client cards; `collectionName` picks the store.
  //
  // Field patches (SHEETS-V2-SLICE-2-SPEC 6.1): the first write of a card
  // that has no stored document sends the whole record, so the document
  // exists with every default; every later write sends only the keys that
  // changed against the local copy, accumulated over the debounce window,
  // through dataService.<collection>.patch. A failed patch keeps its keys
  // (memo.pendingKeys): the next write patches them again, with the values
  // of that moment, together with what it changed itself. It never becomes
  // a whole-record write: that would replay this tab's copy of every field
  // over what another tab saved meanwhile (and could move a fingerprint and
  // resend a sheet). Only a failed FIRST write, with no document yet, is
  // sent whole again (memo.stored stays false, so `stored` reads false).
  // Nothing changed = nothing written, resolved true.
  //
  // Crew call (SHEETS-V2-SLICE-2-SPEC 4.1): once a crew-card write lands, its
  // keys update the job's COMMITTED crew calls and subjects
  // (committedCardCalendarRef), and when the write carried the crew call or
  // the subject (a patch with either key, or a whole record) a calendar sync
  // is queued for the job. Whether anything needs to be sent is decided when
  // that op runs, not here: it reads the job of that moment and compares its
  // sessions' synced fingerprints (what the calendar has) with the committed
  // maps (what it should have), so a write that lands while an earlier
  // request is still running (a revert included) is still checked after that
  // request wrote back. Never this tab's pending edit of another card, which
  // that card's own write publishes; never twice, since the fingerprint
  // written back by a successful call is the baseline of the next.
  const saveCard = useCallback((collectionName, card) => {
    if (!card?.id) return Promise.resolve(false);
    const { _docId, _seeded, _pending, ...rest } = card;
    void _docId; void _seeded; void _pending;
    const isClient = collectionName === "clientCards";
    const ref = isClient ? clientCardsRef : crewCardsRef;
    const state = isClient ? clientCards : crewCards;
    // Server-owned fields (SERVER_OWNED_CARD_FIELDS) never travel from the
    // client; the local copy keeps whatever the listener last delivered.
    const record = { ...stripServerOwned(rest), updatedAt: new Date().toISOString() };
    const prev = ref.current || [];
    const i = prev.findIndex(c => c.id === record.id);
    const local = i >= 0 ? prev[i] : null;
    const key = `${collectionName}/${record.id}`;
    const memo = cardWriteMemo.current[key] || (cardWriteMemo.current[key] = {});
    // Stored: loaded from Firestore (_docId) or written whole from this tab.
    const stored = !!local && !local._seeded && (!!local._docId || !!memo.stored);
    const changed = local ? changedCardKeys(local, record) : Object.keys(record);
    const next = i >= 0 ? prev.map(c => c.id === record.id ? { ...c, ...record } : c) : [...prev, record];
    ref.current = next;
    state.setData(next);
    const pending = crewCardSaveTimers.current[key];
    // A pending entry whose timer already fired is an in-flight write: the
    // listener keeps protecting local edits, but this edit gets its own
    // promise so Deploy waits for THIS write, not the earlier one.
    const reuse = pending && !pending.fired;
    if (pending && !pending.fired) clearTimeout(pending.timer);
    let resolveFn;
    const done = reuse ? pending.done : new Promise(r => { resolveFn = r; });
    const resolve = reuse ? pending.resolve : resolveFn;
    const entry = {
      timer: null, done, resolve, fired: false,
      whole: !stored || !!(reuse && pending.whole),
      keys: new Set([...(reuse ? pending.keys : []), ...changed]),
      // The card as the window found it: a key edited and edited back within
      // the window compares equal to it and is dropped from the patch.
      baseline: reuse ? pending.baseline : local,
    };
    entry.timer = setTimeout(() => {
      entry.fired = true;
      const latest = ref.current.find(c => c.id === record.id) || record;
      // Writes to one card are serialized so a slow earlier write can never
      // land after (and clobber) a newer one.
      const chain = crewCardWriteChains.current[key] || Promise.resolve();
      crewCardWriteChains.current[key] = chain.then(async () => {
        // Decided here, once every earlier write for this card has settled:
        // a failed one hands over the keys it still owes. Whole only while
        // no write has created the document: a first write that landed
        // meanwhile turns every window queued behind it into a patch of its
        // own keys, so it can never replay this tab's copy of a field
        // another tab saved since. A failed first write leaves memo.stored
        // unset, and the record is sent whole again.
        const whole = entry.whole && !memo.stored;
        // The keys this patch carries: what the window changed against the
        // card it found (an edit undone within the window compares equal and
        // drops out) plus what a failed patch still owes; every value from
        // `latest`.
        const owed = new Set([
          ...(memo.pendingKeys || []),
          ...[...entry.keys].filter(k => !entry.baseline || !sameCardValue(entry.baseline[k], latest[k])),
        ]);
        const payload = cardWritePayload(latest);
        // What travels: the record, or exactly the owed keys.
        const sent = whole ? payload : pickKeys(payload, [...owed]);
        let ok = false;
        try {
          if (whole) await dataService[collectionName].save(sent);
          else if (owed.size) await dataService[collectionName].patch(latest.id, sent);
          memo.stored = true;
          memo.pendingKeys = null;
          ok = true;
          resolve(true);
        } catch (e) {
          console.error("saveCard:", collectionName, e);
          notify.error("Sheet did not save. Please try again.");
          if (!whole) memo.pendingKeys = owed;
          resolve(false);
        } finally {
          if (crewCardSaveTimers.current[key] === entry) delete crewCardSaveTimers.current[key];
        }
        // Outside the write's own try: a throw here must not reject the
        // chain, or every later write for this card would wait forever. A
        // failed write changes nothing: the committed store still says what
        // the server holds, and the next write that lands for this card
        // carries the owed keys (the crew call among them) and queues the
        // sync then.
        if (isClient) return;
        try {
          // A calendar key travelled: queue the sync. Whether the calendar
          // needs anything is decided when the op runs (syncJobToCalendar).
          const queuedSync = ok && (whole || "crewCall" in sent || "subject" in sent);
          if (ok) committedCardCalendarRef.current = withCommittedCard(committedCardCalendarRef.current, latest, sent, whole);
          if (queuedSync) afterCrewCallWrite.current?.(latest.jobId);
          // A remote change of this card was held back while this write was
          // pending (applyCrewCardsSnapshot). The op just queued decides at
          // run time; with none queued (no calendar key travelled after all,
          // or the write failed and its owed keys wait for the next one) the
          // grace check is scheduled now, so the repair is never lost.
          if (queuedSync) deferredRemoteRepairs.current.delete(latest.jobId);
          else if (deferredRemoteRepairs.current.delete(latest.jobId)) scheduleRemoteCardSync(latest.jobId);
        } catch (e) {
          console.error("saveCard: crew call calendar sync did not start:", e);
        }
      });
    }, 800);
    crewCardSaveTimers.current[key] = entry;
    return done;
  }, []);
  const saveCrewCard = useCallback((card) => saveCard("crewCards", card), [saveCard]);
  const saveClientCard = useCallback((card) => saveCard("clientCards", card), [saveCard]);

  // Pending card writes must not fire after the provider is gone (sign-out).
  useEffect(() => () => {
    Object.values(crewCardSaveTimers.current).forEach(p => { clearTimeout(p.timer); p.resolve(false); });
    crewCardSaveTimers.current = {};
    Object.values(jobSaveTimers.current).forEach(e => { if (!e.fired) { clearTimeout(e.timer); e.resolve(false); } });
    jobSaveTimers.current = {};
    remoteCardSyncTimers.current.forEach(timer => clearTimeout(timer));
    remoteCardSyncTimers.current.clear();
    deferredRemoteRepairs.current.clear();
  }, []);

  // Deployed card links by session id for one job — feeds the calendar event
  // description so a later client-side sync cannot erase the "Call sheet:" line.
  const crewCardUrlsFor = useCallback((jobDocId) => {
    const out = {};
    (crewCardsRef.current || []).forEach(c => {
      if (c.jobId === jobDocId && c.lastDeploy?.url && !c.lastDeploy?.revokedAt) out[c.sessionId] = c.lastDeploy.url;
    });
    return out;
  }, []);

  /* ════════════════════════════════════════
     CONTACT CRUD
  ════════════════════════════════════════ */
  const saveContact = useCallback(async (contact) => {
    if (!contact.name || !contact.name.trim()) {
      throw new Error("Contact must have a non-empty name");
    }
    await dataService.contacts.save(contact);
    contacts.setData(prev => {
      const idx = prev.findIndex(c => c.id === contact.id);
      return idx >= 0 ? prev.map(c => c.id === contact.id ? contact : c) : [...prev, contact];
    });
  }, []);

  const removeContact = useCallback(async (contactId) => {
    const snapshot = contacts.data;
    contacts.setData(prev => prev.filter(c => c.id !== contactId));
    try {
      await dataService.contacts.delete(contactId);
    } catch (e) {
      contacts.setData(snapshot);
      notify.error("Failed to remove contact. Please try again.");
    }
  }, [contacts.data]);

  /* ════════════════════════════════════════
     CLIENT CRUD (v2 data model)
  ════════════════════════════════════════ */
  const saveClient = useCallback(async (client) => {
    if (!client.name || !client.name.trim()) {
      throw new Error("Client must have a non-empty name");
    }
    // Auto-generate clientCode if missing
    let toSave = { ...client };
    if (!toSave.clientCode && toSave.name) {
      toSave.clientCode = mkClientCode(toSave.name);
    }
    // Associated-studio link: one level, no self-reference, no chains — enforced
    // here so a bad value can't arrive from any caller or a hand-edited doc.
    toSave = sanitizeParentClientId(toSave, clients.data);
    await dataService.clients.save(toSave);
    clients.setData(prev => {
      const idx = prev.findIndex(c => c.id === toSave.id);
      return idx >= 0 ? prev.map(c => c.id === toSave.id ? toSave : c) : [...prev, toSave];
    });
  }, [clients.data]);

  const removeClient = useCallback(async (clientId) => {
    const snapshot = clients.data;
    clients.setData(prev => prev.filter(c => c.id !== clientId));
    try {
      await dataService.clients.delete(clientId);
    } catch (e) {
      clients.setData(snapshot);
      notify.error("Failed to remove client. Please try again.");
    }
  }, [clients.data]);

  /* ════════════════════════════════════════
     SEGMENT CRUD (CRM v3 slice 4)
     A segment record is small and rule-shaped, so there is no debounce and no
     partial patching: the editor hands over the whole thing on Save. Delete is
     a SOFT delete — a campaign freezes its audience but keeps the segment id,
     and a hard delete would leave that reference pointing at nothing.
     ════════════════════════════════════════ */

  const saveSegment = useCallback(async (segment) => {
    if (!segment?.id) return;
    const now = new Date().toISOString();
    const toSave = { ...segment, createdAt: segment.createdAt || now, updatedAt: now };
    await dataService.segments.save(toSave);
    segments.setData(prev => {
      const idx = prev.findIndex(s => s.id === toSave.id);
      return idx >= 0 ? prev.map(s => s.id === toSave.id ? toSave : s) : [...prev, toSave];
    });
    return toSave;
    // setData is a useState setter and stable; naming it keeps this callback
    // stable too, which useCollectionState's fresh object would otherwise break.
  }, [segments.setData]);

  const removeSegment = useCallback(async (segmentId) => {
    const record = segments.data.find(s => s.id === segmentId);
    if (!record) return;
    const deleted = { ...record, deletedAt: new Date().toISOString() };
    segments.setData(prev => prev.map(s => s.id === segmentId ? deleted : s));
    try {
      await dataService.segments.save(deleted);
    } catch {
      /* Roll back THIS record only, and only while it is still the optimistic
         object we put there. Restoring the whole pre-delete snapshot erased
         every other change that landed in between — another segment saved in
         this tab, or anything the Firestore subscription delivered while the
         write was in flight. Identity is the test: if something replaced the
         record, that something is newer than this failure. */
      segments.setData(prev => prev.map(s => (s === deleted ? record : s)));
      notify.error("Failed to delete the segment. Please try again.");
    }
  }, [segments.data, segments.setData]);

  /* ════════════════════════════════════════
     QUOTE CRUD
     Mutators return true when the write persisted and false when it rolled
     back, so callers (single + bulk actions in SavedQuotes) can toast honestly
     instead of claiming success while the row silently reverts.
  ════════════════════════════════════════ */
  // The list actions write only their own fields, through the one saving module
  // (lib/quotePersistence.js patchQuoteFields): a quote being saved, booked or restored
  // refuses them until it is done, and the builder's copy of the same quote follows.
  const deleteQuote = useCallback((id) => workspaceRef.current.persistence.patchQuoteFields(id, { deletedAt: new Date().toISOString() }), []);
  const restoreQuote = useCallback((id) => workspaceRef.current.persistence.patchQuoteFields(id, { deletedAt: null }), []);
  const updateQuoteStatus = useCallback((id, status) => workspaceRef.current.persistence.patchQuoteFields(id, { jobStatus: status }), []);
  const updateQuoteField = useCallback((id, fields) => workspaceRef.current.persistence.patchQuoteFields(id, fields), []);

  const permanentDeleteQuote = useCallback(async (id) => {
    if (!isAdmin) return false;
    if (workspaceRef.current?.persistence.isLocked(id)) {
      notify.error("This quote is still saving, booking or opening. Try again when it has finished.");
      return false;
    }
    // The quote open in the builder: deleting it would strand its edits (a save can't
    // recreate a deleted record), so it is refused with the way out.
    if (workspaceRef.current?.persistence.isOpenQuote(id)) {
      notify.error("This quote is open in the builder. Open another quote (or New) first, then delete it.");
      return false;
    }
    const snapshot = quotesLive.current;
    setQuotesData(prev => prev.filter(sq => sq.id !== id));
    try { await dataService.quotes.delete(id); return true; }
    catch (e) { console.error("permanentDeleteQuote:", e); setQuotesData(snapshot); return false; }
  }, [quotes.data]);

  /* Follow-up helpers: all date-only values go through localDayISO — UTC
     serialization lands a day late during US evenings (codex F7). Each returns
     the updateQuoteField outcome so callers can toast honestly (codex F6). */
  const markFollowedUp = useCallback((id) => {
    const sq = quotes.data.find(s => s.id === id);
    if (!sq) return false;
    const history = [...(sq.followUpHistory || [])];
    history.push({ date: localDayISO(), note: `Followed up (was due ${sq.followUpDate || "unset"})` });
    return updateQuoteField(id, { followUpDate: "", followUpHistory: history });
  }, [quotes.data, updateQuoteField]);

  const snoozeFollowUp = useCallback((id, days) => {
    const sq = quotes.data.find(s => s.id === id);
    if (!sq) return false;
    const newDate = localDayISO(new Date(Date.now() + days * 86400000));
    const history = [...(sq.followUpHistory || [])];
    history.push({ date: localDayISO(), note: `Snoozed to ${newDate}` });
    return updateQuoteField(id, { followUpDate: newDate, followUpHistory: history });
  }, [quotes.data, updateQuoteField]);

  const snoozeBizDays = useCallback((id, bizDays) => {
    const sq = quotes.data.find(s => s.id === id);
    if (!sq) return false;
    const newDate = addBizDays(bizDays);
    const history = [...(sq.followUpHistory || [])];
    history.push({ date: localDayISO(), note: `Snoozed to ${newDate} (+${bizDays} biz days)` });
    return updateQuoteField(id, { followUpDate: newDate, followUpHistory: history });
  }, [quotes.data, updateQuoteField]);

  const snoozeToDate = useCallback((id, dateStr) => {
    const sq = quotes.data.find(s => s.id === id);
    if (!sq) return false;
    const history = [...(sq.followUpHistory || [])];
    history.push({ date: localDayISO(), note: `Follow-up set to ${dateStr}` });
    return updateQuoteField(id, { followUpDate: dateStr, followUpHistory: history });
  }, [quotes.data, updateQuoteField]);

  // Propagate a quote's total to its linked job's grossRevenue. Keeps the Jobs
  // list and Insights consistent with the live quote (including after amendments).
  // Updates Firestore AND local state — the jobs listener can skip snapshots
  // during the updateJob debounce window, so relying on it alone loses writes.
  const syncJobRevenueFromQuote = useCallback(async (quoteId, newTotal) => {
    const localLinkedJob = jobs.data.find(j => j.quoteId === quoteId && !j.deletedAt);
    const linkedJob = await refreshLinkedJob(localLinkedJob, quoteId);
    if (!linkedJob) return { synced: false, skipped: false, reason: "no-linked-job" };
    if (jobHasRevenueSyncGuard(linkedJob)) {
      return { synced: false, skipped: true, reason: "invoiced_or_paid", linkedJob };
    }
    // Pro bono (2026-09-11): the JOB's billing flag is the billing record. A
    // pro bono job stays at $0 whatever its quote now totals (GO review: the
    // sync used to write the quote's figure onto a job flipped free on the card).
    const target = isProBono(linkedJob) ? 0 : newTotal;
    if (linkedJob.grossRevenue === target) {
      return { synced: false, skipped: false, reason: "unchanged", linkedJob };
    }
    const updated = { ...linkedJob, grossRevenue: target };
    const localJobId = localLinkedJob.id || localLinkedJob._docId;
    setJobsData(prev => prev.map(j => (j.id || j._docId) === localJobId ? updated : j));
    try {
      await dataService.jobs.save(updated);
      return { synced: true, skipped: false, linkedJob: updated };
    } catch (e) {
      console.error("syncJobRevenueFromQuote:", e);
      setJobsData(prev => prev.map(j => (j.id || j._docId) === localJobId ? linkedJob : j));
      notify.error("Quote saved, but job revenue didn't update. Try again.");
      return { synced: false, skipped: false, reason: "error", linkedJob, error: e };
    }
  }, [jobs.data]);

  const forceSyncJobRevenueFromQuote = useCallback(async (quoteId, newTotal) => {
    const localLinkedJob = jobs.data.find(j => j.quoteId === quoteId && !j.deletedAt);
    const linkedJob = await refreshLinkedJob(localLinkedJob, quoteId);
    if (!linkedJob) return { synced: false, skipped: false, reason: "no-linked-job" };
    const target = isProBono(linkedJob) ? 0 : newTotal; // same rule as the guarded sync
    if (linkedJob.grossRevenue === target) {
      return { synced: false, skipped: false, reason: "unchanged", linkedJob };
    }
    const updated = { ...linkedJob, grossRevenue: target };
    const localJobId = localLinkedJob.id || localLinkedJob._docId;
    setJobsData(prev => prev.map(j => (j.id || j._docId) === localJobId ? updated : j));
    try {
      await dataService.jobs.save(updated);
      return { synced: true, skipped: false, linkedJob: updated };
    } catch (e) {
      console.error("forceSyncJobRevenueFromQuote:", e);
      setJobsData(prev => prev.map(j => (j.id || j._docId) === localJobId ? linkedJob : j));
      notify.error("Could not update job revenue. Try again.");
      return { synced: false, skipped: false, reason: "error", linkedJob, error: e };
    }
  }, [jobs.data]);

  /* ── Contact-match commit ──────────────────────────────────────────────────
     The consumer for the Link / New / No-Link decisions made in the builder's
     contact-match panel. Those decisions have been persisted on quotes since
     2026-06-01 and read by nothing: the last contact the CRM ever received is
     dated 2026-07-13, while 14 people marked "New" were silently dropped.

     `planContactCommits` decides everything (pure, unit-tested in
     contactCommit.test.js); this function only performs the writes, in the
     order the plan returns them, so a client always exists before the people
     that carry its id. Failures are collected rather than thrown — a CRM write
     must never cost the operator a saved quote or a booked job — but the caller
     is handed `failed` so it can say so out loud. */
  const commitContactMatches = useCallback(async (quote, matches) => {
    const plan = planContactCommits(quote, matches, contacts.data, clients.data);
    const failed = [];
    // The records actually written, returned to the caller. React state updates
    // below are NOT visible in the caller's closure within the same tick, so
    // anything the caller does next (BLANK_JOB, role upgrades, clientCode
    // lookups) must read these, not contacts.data/clients.data (Gemini review
    // 2026-08-26, G1/G2 — stale-closure drops of just-created records).
    const written = { contacts: [], clients: [] };
    const failedClientIds = new Set();
    for (const op of plan.ops) {
      try {
        if (op.kind === "client") {
          const rec = op.record.clientCode ? op.record : { ...op.record, clientCode: mkClientCode(op.record.name) };
          await dataService.clients.save(rec);
          written.clients.push(rec);
          clients.setData(prev => {
            const idx = prev.findIndex(c => c.id === rec.id);
            return idx >= 0 ? prev.map(c => (c.id === rec.id ? rec : c)) : [...prev, rec];
          });
        } else {
          // Never persist a pointer at a client whose create just failed (G3).
          const rec = stripFailedClientRefs(op.record, failedClientIds);
          await dataService.contacts.save(rec);
          written.contacts.push(rec);
          contacts.setData(prev => {
            const idx = prev.findIndex(c => c.id === rec.id);
            return idx >= 0 ? prev.map(c => (c.id === rec.id ? rec : c)) : [...prev, rec];
          });
        }
      } catch (e) {
        console.error(`CRM ${op.mode} ${op.kind} failed for ${op.record.id}:`, e);
        failed.push({ kind: op.kind, mode: op.mode, id: op.record.id, name: op.record.name, error: e });
        if (op.kind === "client" && op.mode === "create") failedClientIds.add(op.record.id);
      }
    }
    // A create that FAILED must not leave its never-written id on the quote/job —
    // that would be a dangling link a later save would faithfully persist.
    // Only a failed CREATE leaves no record to point at; a failed update (dates,
    // consent) leaves the existing contact in place, so its link stands.
    const failedIds = new Set(failed.filter((f) => f.mode === "create").map((f) => f.id));
    return {
      ...plan,
      contactEntityId: failedIds.has(plan.contactEntityId) ? null : plan.contactEntityId,
      companyEntityId: failedIds.has(plan.companyEntityId) ? null : plan.companyEntityId,
      linkedContactIds: plan.linkedContactIds.filter((id) => !failedIds.has(id)),
      written,
      failed,
    };
  }, [contacts.data, clients.data]);

  /* ═══ THE BUILDER'S QUOTE, ITS SAVES AND ITS LOCK ═══
     lib/quotePersistence.js through useQuoteWorkspace: Ingest, Save draft, the Unsaved
     dialog, every way of replacing the open quote, version Restore, the list actions'
     field writes, and the recovery copy. `quotesLive` mirrors the list so the saving code
     reads what it just wrote. */
  const workspace = useQuoteWorkspace({
    isAdmin, loaded,
    quotes: quotesLive.current,
    getQuotes: () => quotesLive.current,
    setQuotes: setQuotesData,
    jobs: jobs.data, contacts: contacts.data, clients: clients.data,
    storage: dataService, getImages, saveImages, deleteAttachmentFile, notify,
    commitContactMatches, syncJobRevenueFromQuote,
    getEquipmentMap: () => getEqNames(),
  });
  workspaceRef.current = workspace;
  const { q, setQ, getQ, busy: quoteBusy, unsavedPrompt, persistence, setBuilderSaveBlocker, highlightMissing, setHighlightMissing } = workspace;
  const {
    saveQuote, saveDraft, loadQuote, loadDraft, duplicateQuote, amendQuote, createChangeOrderQuote,
    startQuoteFromProspect, newQuote, restoreQuoteVersion, discardEdits, updateQuote, normalizeOpenQuote,
    onReplaceStart: onBuilderReplaceStart, unsavedEditsNotice, isDirty: isQuoteDirty, hadRecord: quoteHadRecord,
  } = persistence;

  const deleteDraft = useCallback(async (draftId) => {
    if (!isAdmin) return false;
    if (persistence.isLocked(draftId)) {
      notify.error("This draft is still saving or opening. Try again when it has finished.");
      return false;
    }
    if (persistence.isOpenQuote(draftId)) {
      notify.error("This draft is open in the builder. Open another quote (or New) first, then delete it.");
      return false;
    }
    const snapshot = quotesLive.current;
    setQuotesData(prev => prev.filter(sq => sq.id !== draftId));
    try { await dataService.quotes.delete(draftId); return true; }
    catch (e) { console.error("deleteDraft:", e); setQuotesData(snapshot); return false; }
  }, [quotes.data]);

  /* ════════════════════════════════════════
     JOB CRUD
  ════════════════════════════════════════ */
  const updateJob = useCallback((jobId, updates) => {
    validateJobFields(updates);
    setJobsData(prev => {
      const current = prev.find(j => j.id === jobId);
      if (current && isJobPatchNoOp(current, updates)) return prev;
      if (!prevJobRef.current.has(jobId)) {
        if (current) prevJobRef.current.set(jobId, current);
      }
      const next = prev.map(j => {
        if (j.id !== jobId) return j;
        const merged = { ...j, ...updates };
        // Auto-sync jobId when captureDate or clientCode changes.
        // Project is passive metadata and does not touch clientCode or jobId.
        if (("captureDate" in updates || "clientCode" in updates) && merged.clientCode) {
          merged.jobId = mkJobId(merged.clientCode, merged.captureDate || "");
        }
        // Mirror single-day root edits to sessions[0] so calendar sync (which diffs sessions[0]) fires.
        // Done here (inside the setter) rather than at the call site so concurrent edits within
        // the 800ms debounce window always read the latest sessions[0] instead of a stale closure.
        const rootTouchesSession =
          !("sessions" in updates) &&
          !merged.multiDay &&
          Array.isArray(merged.sessions) && merged.sessions.length === 1 &&
          Object.keys(updates).some(k =>
            SESSION_LOCATION_FIELDS.includes(k) ||
            SESSION_SCHEDULE_FIELDS.includes(k) ||
            k === "isStudio"
          );
        if (rootTouchesSession) {
          const s0 = merged.sessions[0];
          const s0Patch = {};
          for (const f of SESSION_LOCATION_FIELDS) if (f in updates) s0Patch[f] = updates[f];
          for (const f of SESSION_SCHEDULE_FIELDS) {
            if (f in updates) s0Patch[f === "captureDate" ? "date" : f] = updates[f];
          }
          if ("isStudio" in updates) s0Patch.isStudio = updates.isStudio;
          merged.sessions = [{ ...s0, ...s0Patch }];
        }
        return migrateJob(merged);
      });
      const existing = jobSaveTimers.current[jobId];
      // An entry still in its debounce window is replaced and keeps its promise.
      // One whose write is already in flight is left alone: this edit gets its
      // own promise, so flushJobSave waits for THIS write, not the earlier one.
      const reuse = !!existing && !existing.fired;
      if (reuse) clearTimeout(existing.timer);
      let resolveFn; const promise = reuse ? existing.promise : new Promise(r => { resolveFn = r; });
      // `job`: the job as this edit left it. The write sends it with the
      // sessions' sync fields as this tab holds them when it executes (below);
      // a jobs snapshot arriving inside the window moves them here too
      // (mergeRemoteSyncState), nothing else.
      const entry = { jobId, timer: null, promise, resolve: reuse ? existing.resolve : resolveFn, fired: false, job: null };
      jobSaveTimers.current[jobId] = entry;
      const updated = next.find(j => j.id === jobId);
      if (!updated) { settleJobSave(entry, false); return next; }
      entry.job = updated;
      entry.timer = setTimeout(() => {
        entry.fired = true;
        const chain = jobWriteChains.current[jobId] || Promise.resolve();
        jobWriteChains.current[jobId] = chain.then(async () => {
          let ok = false;
          // The payload takes the sessions' sync fields as this tab holds them
          // NOW (jobsRef; a single-day job's root pair included): a fingerprint
          // an op ahead in the calendar queue wrote back since the edit (a
          // crew-card sync that ran inside the debounce) or one another tab
          // delivered. The copy the edit left would put the older fingerprint
          // back in the document, and its listener echo would roll this tab's
          // baseline back, so the sync behind it sent the change again.
          const latest = jobsRef.current.find(j => j.id === jobId);
          const payload = latest ? withRemoteSyncState(entry.job, latest) : entry.job;
          try {
            await dataService.jobs.save(payload);
            ok = true;
            const prevSnapshot = prevJobRef.current.get(jobId);
            prevJobRef.current.delete(jobId);
            // Through the queue (not syncJobToCalendar directly) so a delete/unbook
            // firing right after this edit waits for the create/update to write back
            // its event id before tearing it down — same race the queue closes for book.
            syncJobToCalendarInBackground(prevSnapshot, payload, `edit job ${jobId}`);
          } catch (e) {
            console.error("updateJob save failed:", e);
            notify.error("Failed to save job changes. Your edits may not have been saved.");
          }
          settleJobSave(entry, ok);
        });
      }, 800);
      return next;
    });
  }, []);

  // Resolves once THIS job has no save pending: true when the write landed (or
  // nothing was pending and the last outcome was not a failure), false otherwise.
  const flushJobSave = useCallback((jobId) => {
    const e = jobSaveTimers.current[jobId];
    if (e) return e.promise;
    return Promise.resolve(jobSaveOutcome.current[jobId] !== false);
  }, []);

  // Defensive-merge helper — reads the job as this tab holds it now (setJobsData runs the updater at the call, on jobsRef), applies a session-level transform, persists to Firestore.
  // Shared by calendar-sync write-back and delete-cleanup.
  const patchJobSessions = useCallback(async (jobId, sessionMapper, { allowSoftDeleted = false } = {}) => {
    let persisted = null;
    setJobsData(prev => {
      const latest = prev.find(j => j.id === jobId);
      if (!latest) return prev;
      if (!allowSoftDeleted && latest.deletedAt) return prev;
      const nextSessions = (latest.sessions || []).map(sessionMapper);
      const unchanged = nextSessions.length === (latest.sessions || []).length
        && nextSessions.every((s, i) => s === latest.sessions[i]);
      if (unchanged) return prev;
      persisted = { ...latest, sessions: nextSessions };
      return prev.map(j => j.id === jobId ? persisted : j);
    });
    if (!persisted) return { ok: true, persisted: null };
    try {
      await dataService.jobs.save(persisted);
      return { ok: true, persisted };
    } catch (e) {
      console.error("Failed to persist session patch:", e);
      return { ok: false, error: e };
    }
  }, []);

  // Sibling of patchJobSessions for writes that land at the job root (single-day gcal state).
  // Concurrency caveat: shares patchJobSessions' lost-write window — if two calls overlap,
  // the later save can overwrite the earlier one in Firestore. Callers today are serialized
  // (one sync per debounce, one cleanup per delete), so we accept the risk.
  const patchJobRoot = useCallback(async (jobId, rootMapper, { allowSoftDeleted = false } = {}) => {
    let persisted = null;
    setJobsData(prev => {
      const latest = prev.find(j => j.id === jobId);
      if (!latest) return prev;
      if (!allowSoftDeleted && latest.deletedAt) return prev;
      const next = rootMapper(latest);
      if (!next || next === latest) return prev;
      persisted = next;
      return prev.map(j => j.id === jobId ? persisted : j);
    });
    if (!persisted) return { ok: true, persisted: null };
    try {
      await dataService.jobs.save(persisted);
      return { ok: true, persisted };
    } catch (e) {
      console.error("Failed to persist root patch:", e);
      return { ok: false, error: e };
    }
  }, []);

  const persistSessionSyncResult = useCallback((jobId, newSessions) => {
    // Single-day hydrated sessions don't exist in Firestore — persist their gcal state at the job root.
    const hydratedResult = newSessions.find(ns => isHydratedSessionId(ns.id));
    if (hydratedResult) {
      return patchJobRoot(jobId, (latest) => {
        const nextEventId = hydratedResult.gcalEventId || null;
        const nextFingerprint = hydratedResult.gcalSyncedFingerprint || null;
        if ((latest.gcalEventId || null) === nextEventId && (latest.gcalSyncedFingerprint || null) === nextFingerprint) {
          return latest;
        }
        const updated = { ...latest, gcalEventId: nextEventId, gcalSyncedFingerprint: nextFingerprint };
        return migrateJob(updated);
      // allowSoftDeleted: a book→immediate-unbook race soft-deletes the job before
      // this write-back runs; the event id MUST still persist so cleanup's reload
      // can find and delete the event it just created (otherwise it orphans).
      }, { allowSoftDeleted: true });
    }
    return patchJobSessions(jobId, (ls) => {
      const updated = newSessions.find(ns => ns.id === ls.id);
      if (!updated) return ls;
      if (ls.gcalEventId === updated.gcalEventId && ls.gcalSyncedFingerprint === updated.gcalSyncedFingerprint) return ls;
      return { ...ls, gcalEventId: updated.gcalEventId, gcalSyncedFingerprint: updated.gcalSyncedFingerprint };
    }, { allowSoftDeleted: true });
  }, [patchJobSessions, patchJobRoot]);

  // Records sync/cleanup errors onto the integration doc. Re-reads it first
  // because kv.set is a full-doc overwrite (the whole object lives in one `value`
  // field): blindly writing `{ ...staleIntegration }` from a background task could
  // clobber a concurrent writer or resurrect a calendar the user just disabled.
  const recordCalendarSyncErrors = useCallback(async (errors) => {
    const fresh = await dataService.kv.get(KV_KEY_GCAL_INTEGRATION);
    if (!fresh?.enabled || !fresh.calendarId) return;
    await dataService.kv.set(KV_KEY_GCAL_INTEGRATION, {
      ...fresh,
      lastSyncAt: new Date().toISOString(),
      lastSyncErrors: errors,
    });
  }, []);

  /* Every calendar sync, at the head of the job's serial queue. Two records
     meet here, both read when the op RUNS: the committed crew calls and
     subjects of the job's cards (committedCalendarFor: what the calendar
     should have) and the job as this tab holds it now (jobsRef: its sessions'
     synced fingerprints are what the calendar has, written back by the op
     ahead in the queue or delivered by the jobs listener from another tab's
     sync). syncJobSessions diffs each session's fingerprint against the
     committed maps, so a change is sent only while its fingerprint write-back
     has not landed, and a session whose fingerprint another tab wrote back is
     not sent again here.

     A job save passes the job before and after the edit; both take the
     persisted sync state of the job of this moment (withPersistedCalendarState),
     so a fingerprint written back since the save was captured is the
     baseline, and an event id written back since is not created twice.

     `cardsOnly` (a crew-card write landed carrying a calendar key; the job
     did not change): `currJob` names the job, the record is read here, and
     the op ends before any read when the job is gone or in the trash, or
     when no session differs from the committed maps (calendarNeedsSync): a
     revert that lands while an earlier request runs is caught this way, once
     that request wrote back, and a change an op ahead already published is
     not sent twice. Write-back and error recording are the job-save path's.

     The job of this moment is read AFTER the roster read, the last await
     before the calendar request (the cardsOnly check runs again there, and
     the job save's overlay is taken there): a write that lands while this op
     waits for the integration or the roster, or a fingerprint the jobs
     listener delivers meanwhile (another tab's successful sync of the same
     change), is the baseline, so the change is not sent again from here. */
  const syncJobToCalendar = useCallback(async (prevJob, currJob, cardsOnly = false) => {
    const jobId = currJob.id;
    const latestJob = () => {
      const found = jobsRef.current.find(j => j.id === jobId);
      return found ? migrateJob(found) : null;
    };
    const cardsDiffer = (latest) => !!latest && !latest.deletedAt && calendarNeedsSync(latest, committedCalendarFor(jobId));
    try {
      if (cardsOnly && !cardsDiffer(latestJob())) return;
      const integration = await dataService.kv.get(KV_KEY_GCAL_INTEGRATION);
      if (!integration?.enabled || !integration.calendarId) return;
      const crewRoster = await dataService.kv.get(KV_KEY_CREW) || [];
      // No await between here and the request: what is compared is the job
      // as this tab holds it now.
      const latest = latestJob();
      let prev = prevJob;
      let curr = currJob;
      if (cardsOnly) {
        if (!cardsDiffer(latest)) return;
        prev = latest;
        curr = latest;
      } else if (latest) {
        prev = withPersistedCalendarState(prev, latest);
        curr = withPersistedCalendarState(curr, latest);
      }
      const committed = committedCalendarFor(jobId);
      const { newSessions, errors, changed } = await syncJobSessions({
        prevJob: prev, currJob: curr, integration,
        contacts: contactsRef.current,
        crewRoster,
        crewCardUrls: crewCardUrlsFor(jobId),
        crewCalls: committed.crewCalls,
        crewSubjects: committed.crewSubjects,
        jobUrlBase: window.location.origin + "/",
        callCalendar,
      });
      const writeback = await persistSessionSyncResult(jobId, newSessions);
      if (!writeback.ok) {
        notify.error("Calendar synced but couldn't save event IDs. Next save may create duplicate events.");
      }
      if (changed || errors.length) {
        await recordCalendarSyncErrors(errors);
      }
    } catch (e) {
      console.error("Calendar sync failed:", e);
    }
  }, [persistSessionSyncResult, recordCalendarSyncErrors]);

  // Chains opFn onto this job's serial calendar-op queue so create/update and
  // delete cleanup for the same job run in submission order (never concurrently).
  // Returns the tail promise; it never rejects (errors are logged), so awaiters
  // and the .finally cleanup always run.
  const enqueueCalendarOp = useCallback((jobId, opFn) => {
    const prev = calendarOpQueue.current.get(jobId) || Promise.resolve();
    const next = prev.then(opFn).catch(e => console.error("Calendar op failed:", e));
    calendarOpQueue.current.set(jobId, next);
    next.finally(() => {
      if (calendarOpQueue.current.get(jobId) === next) calendarOpQueue.current.delete(jobId);
    });
    return next;
  }, []);

  const syncJobToCalendarInBackground = useCallback((prevJob, currJob, context = "job") => {
    const normalized = migrateJob(currJob);
    return enqueueCalendarOp(normalized.id, () => syncJobToCalendar(prevJob, normalized));
  }, [enqueueCalendarOp, syncJobToCalendar]);

  /* A crew-card write has landed carrying the crew call or the subject
     (saveCard): sync the job's calendar through the same serial queue as
     every other calendar op. Only the job id is captured; syncJobToCalendar
     reads the job when the op runs and decides then whether any session's
     event differs from the committed cards (never the cards' local copies,
     where a later, still-pending edit could already sit). A subject change
     alone refreshes the wording under a moved start through calendarSync's
     content key, with no attendee notified (not a time change). Nothing runs
     for a job in the trash: its events were deleted and must not come back. */
  afterCrewCallWrite.current = (jobDocId) => enqueueCalendarOp(jobDocId, () => syncJobToCalendar(null, { id: jobDocId }, true));

  /* A job edited from outside the Suite (the /crm job mode) leaves its session
     marked "re-push me" (see needsCalendarReconcile). The first open of the
     job here pushes the event with no previous job to diff against — the same
     path the backfill uses — and the write-back clears the mark. Same serial
     queue as every other calendar op, so it cannot race a save. */
  const reconcileJobCalendar = useCallback((job) => {
    if (!job || !needsCalendarReconcile(job)) return Promise.resolve();
    // Diff against the job minus its marked sessions, so only those re-push.
    const normalized = migrateJob(job);
    return syncJobToCalendarInBackground(reconcilePrevJob(normalized), normalized, "reconcile");
  }, [syncJobToCalendarInBackground]);

  const cleanupJobCalendar = useCallback(async (job, context = "job") => {
    try {
      // Ordering is guaranteed by the per-job calendarOpQueue — this runs only
      // after any in-flight create/update for the same job, so the fresh reload
      // below observes the latest written-back gcalEventIds.
      const integration = await dataService.kv.get(KV_KEY_GCAL_INTEGRATION);
      if (!integration?.enabled || !integration.calendarId) return;
      const freshJob = await dataService.jobs.get(job.id).catch(() => null);
      const targetJob = freshJob ? migrateJob({ ...freshJob, id: freshJob.id || freshJob._docId || job.id }) : job;
      const { deleted, errors } = await deleteJobEvents({ job: targetJob, integration, callCalendar });
      // Clear gcalEventIds only for events confirmed deleted — preserves stale-id recovery on partial failures.
      if (deleted.sessions.length) {
        await patchJobSessions(
          job.id,
          (s) => deleted.sessions.includes(s.id) ? { ...s, gcalEventId: null, gcalSyncedFingerprint: null } : s,
          { allowSoftDeleted: true }
        );
      }
      if (deleted.root) {
        await patchJobRoot(
          job.id,
          (latest) => (latest.gcalEventId || latest.gcalSyncedFingerprint)
            ? { ...latest, gcalEventId: null, gcalSyncedFingerprint: null }
            : latest,
          { allowSoftDeleted: true }
        );
      }
      if (errors.length) await recordCalendarSyncErrors(errors.map(e => ({ ...e, context })));
    } catch (e) {
      console.error(`Calendar cleanup for ${context} failed:`, e);
    }
  }, [patchJobSessions, patchJobRoot, recordCalendarSyncErrors]);

  const cleanupJobCalendarInBackground = useCallback((job, context = "job") => {
    enqueueCalendarOp(job.id, () => cleanupJobCalendar(job, context));
  }, [enqueueCalendarOp, cleanupJobCalendar]);

  const backfillCalendar = useCallback(async (onProgress) => {
    const integration = await dataService.kv.get(KV_KEY_GCAL_INTEGRATION);
    if (!integration?.enabled || !integration.calendarId) {
      throw new Error("Calendar integration is not enabled");
    }
    const crewRoster = await dataService.kv.get(KV_KEY_CREW) || [];
    const today = new Date().toISOString().slice(0, 10);
    const targetJobs = jobs.data.filter(j =>
      !j.deletedAt && (j.sessions || []).some(s => s.date && s.date >= today)
    );
    const total = targetJobs.length;
    let completed = 0;
    const allErrors = [];
    onProgress?.({ completed: 0, total, errorCount: 0 });

    for (const currJob of targetJobs) {
      // Bail if integration was disabled mid-backfill — don't keep writing to a calendar the user just turned off.
      const fresh = await dataService.kv.get(KV_KEY_GCAL_INTEGRATION);
      if (!fresh?.enabled || !fresh.calendarId) {
        allErrors.push({ jobId: currJob.id, jobCode: currJob.jobId, op: "backfill", reason: "Integration disabled mid-run" });
        break;
      }
      try {
        const committed = committedCalendarFor(currJob.id);
        const { newSessions, errors } = await syncJobSessions({
          prevJob: null, currJob, integration: fresh,
          contacts: contactsRef.current,
          crewRoster,
          crewCardUrls: crewCardUrlsFor(currJob.id),
          crewCalls: committed.crewCalls,
          crewSubjects: committed.crewSubjects,
          jobUrlBase: window.location.origin + "/",
          callCalendar,
        });
        await persistSessionSyncResult(currJob.id, newSessions);
        allErrors.push(...errors.map(e => ({ ...e, jobId: currJob.id, jobCode: currJob.jobId })));
      } catch (e) {
        allErrors.push({ jobId: currJob.id, jobCode: currJob.jobId, op: "backfill", reason: e.message });
      }
      completed++;
      onProgress?.({ completed, total, errorCount: allErrors.length });
    }

    const freshIntegration = await dataService.kv.get(KV_KEY_GCAL_INTEGRATION) || integration;
    await dataService.kv.set(KV_KEY_GCAL_INTEGRATION, {
      ...freshIntegration,
      lastBackfillAt: new Date().toISOString(),
      lastBackfillErrors: allErrors,
    });
    return { completed, total, errors: allErrors };
  }, [jobs.data, persistSessionSyncResult]);

  // Update production pipeline status (scheduled → in_progress → processing → delivered)
  const updateJobStatus = useCallback((jobId, status) => {
    const updates = { status };
    if (status === "delivered") updates.deliveredDate = new Date().toISOString().split("T")[0];
    updateJob(jobId, updates);
  }, [updateJob]);

  // Toggle independent business milestones (invoiced, paid)
  const toggleJobMilestone = useCallback((jobId, milestone) => {
    const job = jobs.data.find(j => j.id === jobId);
    if (!job) return;
    const today = new Date().toISOString().split("T")[0];
    if (milestone === "invoiced") {
      const wasInvoiced = !!job.invoiced;
      const updates = { invoiced: !wasInvoiced };
      if (!wasInvoiced) updates.invoiceDate = today;
      // Un-invoicing also clears paid
      if (wasInvoiced) { updates.paid = false; updates.paidDate = ""; }
      updateJob(jobId, updates);
    } else if (milestone === "paid") {
      if (!job.invoiced) return; // can't pay without invoicing
      const wasPaid = !!job.paid;
      const updates = { paid: !wasPaid };
      if (!wasPaid) updates.paidDate = today;
      updateJob(jobId, updates);
    }
  }, [updateJob, jobs.data]);

  const deleteJob = useCallback(async (jobId) => {
    await cancelPendingJobSave(jobId);
    const job = jobs.data.find(j => j.id === jobId);
    if (!job) return;
    const updated = { ...job, deletedAt: new Date().toISOString() };
    setJobsData(prev => prev.map(j => j.id === jobId ? updated : j));
    try {
      await dataService.jobs.save(updated);
      cleanupJobCalendarInBackground(job, `delete job ${jobId}`);
    } catch (e) {
      setJobsData(prev => prev.map(j => j.id === jobId ? job : j));
      console.error("Failed to delete job:", e);
    }
  }, [jobs.data, cleanupJobCalendarInBackground]);

  const restoreJob = useCallback(async (jobId) => {
    const job = jobs.data.find(j => j.id === jobId);
    if (!job) return;
    // Unbook/delete deleted this job's events; restore re-creates them. Clear any
    // surviving gcal ids (e.g. left by a partial delete failure) so the sync takes
    // the create path rather than PATCHing an event that may already be gone.
    const restored = {
      ...job,
      deletedAt: null,
      gcalEventId: null,
      gcalSyncedFingerprint: null,
      sessions: Array.isArray(job.sessions)
        ? job.sessions.map(s => ({ ...s, gcalEventId: null, gcalSyncedFingerprint: null }))
        : job.sessions,
    };
    setJobsData(prev => prev.map(j => j.id === jobId ? restored : j));
    try {
      await dataService.jobs.save(restored);
      // Background + queued so it serializes behind any in-flight delete cleanup
      // for this job and archive restore stays snappy.
      syncJobToCalendarInBackground(null, restored, `restore job ${jobId}`);
    } catch (e) {
      setJobsData(prev => prev.map(j => j.id === jobId ? job : j));
      console.error("Failed to restore job:", e);
    }
  }, [jobs.data, syncJobToCalendarInBackground]);

  const permanentDeleteJob = useCallback(async (jobId) => {
    await cancelPendingJobSave(jobId);
    const snapshot = jobs.data;
    const job = jobs.data.find(j => j.id === jobId);
    // Remove from the UI immediately so the trash row vanishes on click — the
    // calendar cleanup + Firestore delete run async (the caller is fire-and-forget).
    setJobsData(prev => prev.filter(j => j.id !== jobId));
    try {
      // Cleanup is queued (waits for any in-flight sync to write back event ids)
      // and MUST run before the Firestore delete so its fresh reload can still
      // read them. Its state write-backs no-op now that the job is out of state —
      // harmless, since the doc is hard-deleted next. Cleanup never throws.
      if (job) await enqueueCalendarOp(job.id, () => cleanupJobCalendar(job, `permanent delete job ${jobId}`));
      await dataService.jobs.delete(jobId);
    }
    catch (e) { console.error("permanentDeleteJob:", e); setJobsData(snapshot); }
  }, [jobs.data, enqueueCalendarOp, cleanupJobCalendar]);

  const bookQuote = useCallback(async (quote, navigateToJob, writeQuoteFields) => {
    // Booking writes the job AND the quote's status; both are admin-only.
    if (!isAdmin) return;
    // Check if already booked (ignore soft-deleted jobs from unbook)
    if (jobs.data.some(j => j.quoteId === quote.id && !j.deletedAt)) return;

    // A job is booked only with its contact linked. Quotes ingested before the
    // rule (2026-10-26) may have a typed name only: edit and link them first.
    const bookBlock = quoteContactBlocker(quote, contacts.data);
    if (bookBlock) {
      const err = new Error(bookBlock);
      err.userMessage = `Edit this quote and link its contact before booking. ${bookBlock}`;
      throw err;
    }
    // CD11 — the contact-match panel's "New" button promises a record will be
    // created on book. Honour it BEFORE the job doc exists, so a CRM failure
    // surfaces here instead of leaving a booked job pointing at nothing. The
    // commit is idempotent: a quote saved and then booked resolves to the same
    // records the second time and writes nothing.
    const commit = await commitContactMatches(quote, quote.contactMatches);
    if (commit.failed.length) {
      notify.error(`${commit.failed.length} CRM record(s) could not be written for this booking. Check the People list.`);
    }
    const quoteForJob = {
      ...quote,
      contactEntityId: quote.contactEntityId || commit.contactEntityId || null,
      companyEntityId: quote.companyEntityId || commit.companyEntityId || null,
    };
    if (!quoteForJob.contactEntityId) {
      const err = new Error(LINK_CONTACT_MESSAGE);
      err.userMessage = "The contact could not be saved to your contacts, so the job was not booked. Try again.";
      throw err;
    }

    // Merged views: the commit's setData is invisible to this closure (React
    // state doesn't update mid-tick), so records created seconds ago would be
    // missing from contacts.data/clients.data — BLANK_JOB would drop the cc'd
    // people it exists to link and mint a clientCode that contradicts the new
    // client's (Gemini review 2026-08-26, G1/G2). Everything below reads these.
    const mergeById = (base, extra) => {
      const seen = new Map(base.map(r => [r.id, r]));
      for (const r of extra) seen.set(r.id, r);
      return [...seen.values()];
    };
    const contactsNow = mergeById(contacts.data, commit.written.contacts);
    const clientsNow = mergeById(clients.data, commit.written.clients);

    // Quote carry-forward: BLANK_JOB reads deliverableFormats and sections
    // from the quote object. deliverableFormats → job.deliverableFormats,
    // sections (from initSections(), possibly user-modified) → job.quotedTerms.
    // These flow into doc generation via assembleJobContext().
    const job = BLANK_JOB(quoteForJob, contactsNow, clientsNow, {
      // Everyone the operator confirmed on this quote — including cc'd people,
      // who are linked to the job but never promoted to contact of record.
      confirmedContactIds: commit.linkedContactIds,
      // Holds: equipmentIds resolve to display names for the job's two equipment
      // fields through the live map; the tentative crew rides along as names.
      equipmentMap: getEqNames(),
    });
    // B4: recompute revenue from the line items so a quote with a missing/stale
    // total can't silently book $0 onto the job; warn (non-blocking) if it is
    // genuinely zero so the operator catches a bad quote before invoicing.
    job.grossRevenue = resolveQuoteRevenue(quote);
    // A pro bono quote totals $0 by design (100% Pro bono discount); the warning
    // is for a quote that was never priced.
    if (!(job.grossRevenue > 0) && !isProBono(quote)) {
      notify.error("Booked — but this quote totals $0. Check the line items before invoicing.");
    }
    // If this is a change order quote, add CO link before saving (avoids double-save)
    if (quote.changeOrderOfJobId) job.changeOrderOf = quote.changeOrderOfJobId;
    // A linked client with no code takes the job's, written BEFORE the job exists: a
    // failure stops the booking with its userMessage while it can still be tried again.
    // The client list follows only once the write worked.
    const codedClient = await saveClientCodeBeforeBooking(job, clientsNow, (c) => dataService.clients.save(c));
    if (codedClient) clients.setData(prev => prev.map(c => c.id === codedClient.id ? codedClient : c));
    setJobsData(prev => [...prev, job]);
    try {
      await dataService.jobs.save(job);
    } catch (e) {
      // Rollback: remove the optimistically-added job
      setJobsData(prev => prev.filter(j => j.id !== job.id));
      throw e;
    }

    // Push the newly-booked job's scheduled sessions to Google Calendar.
    // prevJob is null, so every dated session is treated as new and created.
    // migrateJob hydrates a single-day root captureDate into a session so
    // syncJobSessions can place it (the event id writes back to the job root);
    // multi-day jobs already carry dated sessions with stable ids. No-ops when
    // the integration is disabled or no session has a date. Backgrounded so
    // quote booking can navigate as soon as the job itself is saved.
    syncJobToCalendarInBackground(null, job, `book job ${job.id}`);

    // The quote keeps the contact the booking resolved (a confirmed "New" row is
    // created here when the quote was saved before it), with its booked status.
    const bookedFields = { jobStatus: "booked", contactEntityId: quoteForJob.contactEntityId };
    if (!quote.companyEntityId && quoteForJob.companyEntityId) bookedFields.companyEntityId = quoteForJob.companyEntityId;
    if (!(await writeQuoteFields(bookedFields))) console.warn("Quote status update to 'booked' failed:", quote.id);

    // Upgrade to customer role — but only the people actually linked to this job
    // (CD8). This used to match on the free-text `company` string, so booking one
    // quote silently recategorized every contact who happened to have typed the
    // same employer, whether or not they were involved. `job.linkedContacts` is
    // now the narrow, quote-derived list built by BLANK_JOB.
    //
    // Pro bono (2026-09-11): the same linked people also get the `pro_bono` tag
    // when the booked quote is pro bono. That tag is the CRM's ingest signal
    // (professionals we chose not to charge). One write per contact, and none
    // when neither the role nor the tags change.
    const upgradeIds = new Set((job.linkedContacts || []).map(l => l.contactId));
    const proBono = isProBono(quote);
    for (const ct of contactsNow) {
      if (!upgradeIds.has(ct.id)) continue;
      const roles = ct.role || [];
      let updated = roles.includes("customer") ? ct : { ...ct, role: [...roles.filter(r => r !== "prospect"), "customer"] };
      if (proBono) updated = withProBonoTag(updated);
      if (updated === ct) continue;
      contacts.setData(prev => prev.map(c => c.id === updated.id ? updated : c));
      try { await dataService.contacts.save(updated); }
      catch (err) { console.error("Contact role upgrade failed for", updated.id, err); }
    }

    // Update original job's hasChangeOrders array
    if (quote.changeOrderOfJobId) {
      const origJob = jobs.data.find(j => j.id === quote.changeOrderOfJobId);
      if (origJob) {
        const coList = [...(origJob.hasChangeOrders || []), job.id];
        updateJob(origJob.id, { hasChangeOrders: coList });
      }
    }
    
    // Navigate to the new job if callback provided
    if (navigateToJob) navigateToJob(job);
  }, [jobs.data, contacts.data, clients.data, syncJobToCalendarInBackground, commitContactMatches]);

  // Booking reads the saved row and holds that quote's lock until it is done
  // (lib/quotePersistence.js whileBooking), so a save or a second booking can't start
  // meanwhile and the row's buttons say "Booking" instead of accepting a second click.
  const bookJob = useCallback((row, navigateToJob) =>
    persistence.whileBooking(row, (fresh, writeQuoteFields) => bookQuote(fresh, navigateToJob, writeQuoteFields)),
  [persistence, bookQuote]);

  const unbookJob = useCallback(async (jobId) => {
    await cancelPendingJobSave(jobId);
    const job = jobs.data.find(j => j.id === jobId);
    if (!job) return;
    // The quote's own save or booking still running (or finishing late) would clash
    // with the status change below; refused before the job is touched.
    const p = workspaceRef.current?.persistence;
    if (job.quoteId && p?.isLocked(job.quoteId)) {
      notify.error("This job's quote is still saving, booking or opening. Try again when it has finished.");
      return;
    }
    // Soft-delete the job first (preserves all data)
    const updated = { ...job, deletedAt: new Date().toISOString() };
    setJobsData(prev => prev.map(j => j.id === jobId ? updated : j));
    try {
      await dataService.jobs.save(updated);
      // Only revert quote status AFTER job save is confirmed
      if (job.quoteId && !(await updateQuoteStatus(job.quoteId, "quoted"))) {
        notify.error("The job was unbooked, but its quote still says Booked. Revert it to Open in Saved Quotes.");
      }
      // Now that booking creates calendar events, unbooking must remove them —
      // otherwise book → unbook → rebook orphans the old event and adds a new
      // one. Mirrors deleteJob's cleanup. Fire-and-forget; unbook already saved.
      cleanupJobCalendarInBackground(job, `unbook job ${jobId}`);
    } catch (e) {
      setJobsData(prev => prev.map(j => j.id === jobId ? job : j));
      console.error("Failed to unbook job:", e);
      notify.error("Failed to unbook job. Please try again.");
    }
  }, [jobs.data, updateQuoteStatus, cleanupJobCalendarInBackground]);

  const unlinkQuote = useCallback(async (jobId) => {
    const job = jobs.data.find(j => j.id === jobId);
    if (!job || !job.quoteId) return;
    const quoteId = job.quoteId;
    const p = workspaceRef.current?.persistence;
    if (p?.isLocked(quoteId)) {
      notify.error("This job's quote is still saving, booking or opening. Try again when it has finished.");
      return;
    }
    const snapshot = job;
    const updated = { ...job, quoteId: null };
    setJobsData(prev => prev.map(j => j.id === jobId ? updated : j));
    try {
      await dataService.jobs.save(updated);
      if (!(await updateQuoteStatus(quoteId, "quoted"))) {
        notify.error("The quote was unlinked from the job, but it still says Booked. Revert it to Open in Saved Quotes.");
      }
    } catch (e) {
      setJobsData(prev => prev.map(j => j.id === jobId ? snapshot : j));
      console.error("Failed to unlink quote:", e);
      notify.error("Failed to unlink quote. Please try again.");
    }
  }, [jobs.data, updateQuoteStatus]);

  /* ═══ Direct job save (bypasses debounce — for bulk operations) ═══ */
  const saveJobDirect = useCallback(async (job) => {
    try {
      await dataService.jobs.save(job);
      setJobsData(prev => {
        const idx = prev.findIndex(j => j.id === job.id);
        return idx >= 0 ? prev.map(j => j.id === job.id ? job : j) : [...prev, job];
      });
    } catch (e) {
      console.error("saveJobDirect:", e);
      notify.error("Failed to save job. Please try again.");
    }
  }, []);

  /* CRM Import removed — was writing to deprecated entities collection */



  /* ═══ ACTIVE vs TRASHED vs DRAFT SPLITS ═══ */
  const activeQuotes = useMemo(() => quotes.data.filter(sq => !sq.deletedAt && isActiveQuoteRecord(sq)), [quotes.data]);
  const trashedQuotes = useMemo(() => quotes.data.filter(sq => !!sq.deletedAt), [quotes.data]);
  const draftQuotes = useMemo(() => quotes.data.filter(sq => !sq.deletedAt && !isActiveQuoteRecord(sq)), [quotes.data]);
  const activeJobs = useMemo(() => jobs.data.filter(j => !j.deletedAt), [jobs.data]);
  const trashedJobs = useMemo(() => jobs.data.filter(j => !!j.deletedAt), [jobs.data]);
  const activeAssessments = useMemo(() => assessments.data.filter(a => !a.deletedAt), [assessments.data]);
  const trashedAssessments = useMemo(() => assessments.data.filter(a => !!a.deletedAt), [assessments.data]);

  /* ════════════════════════════════════════
     PROSPECT (ASSESSMENT) CRUD
  ════════════════════════════════════════ */
  const updateAssessmentField = useCallback(async (id, field, value) => {
    const a = assessments.data.find(x => x.id === id);
    if (!a) return;
    const updated = { ...a, [field]: value };
    assessments.setData(prev => prev.map(x => x.id === id ? updated : x));
    try {
      await dataService.assessments.save(updated);
    } catch (e) {
      assessments.setData(prev => prev.map(x => x.id === id ? a : x));
      console.error("Failed to update prospect:", e);
    }
  }, [assessments.data]);

  const deleteAssessment = useCallback(async (id) => {
    const a = assessments.data.find(x => x.id === id);
    if (!a) return;
    const updated = { ...a, deletedAt: new Date().toISOString() };
    assessments.setData(prev => prev.map(x => x.id === id ? updated : x));
    try {
      await dataService.assessments.save(updated);
    } catch (e) {
      assessments.setData(prev => prev.map(x => x.id === id ? a : x));
      console.error("Failed to delete prospect:", e);
    }
  }, [assessments.data]);

  const restoreAssessment = useCallback(async (id) => {
    const a = assessments.data.find(x => x.id === id);
    if (!a) return;
    const restored = { ...a, deletedAt: null };
    assessments.setData(prev => prev.map(x => x.id === id ? restored : x));
    try {
      await dataService.assessments.save(restored);
    } catch (e) {
      assessments.setData(prev => prev.map(x => x.id === id ? a : x));
      console.error("Failed to restore prospect:", e);
    }
  }, [assessments.data]);

  const permanentDeleteAssessment = useCallback(async (id) => {
    const snapshot = assessments.data;
    assessments.setData(prev => prev.filter(x => x.id !== id));
    try { await dataService.assessments.delete(id); }
    catch (e) { console.error("permanentDeleteAssessment:", e); assessments.setData(snapshot); }
  }, [assessments.data]);

  /* ════════════════════════════════════════
     CAMPAIGN CRUD (no real-time listener — load-only)
  ════════════════════════════════════════ */
  const saveCampaign = useCallback(async (campaign) => {
    const existing = campaigns.data.find(c => c.id === campaign.id);
    campaigns.setData(prev => {
      const idx = prev.findIndex(c => c.id === campaign.id);
      return idx >= 0 ? prev.map(c => c.id === campaign.id ? campaign : c) : [...prev, campaign];
    });
    try {
      await dataService.campaigns.save(campaign);
    } catch (e) {
      // Rollback on failure
      if (existing) campaigns.setData(prev => prev.map(c => c.id === campaign.id ? existing : c));
      else campaigns.setData(prev => prev.filter(c => c.id !== campaign.id));
      console.error("Failed to save campaign:", e);
    }
  }, [campaigns.data]);

  const deleteCampaign = useCallback(async (id) => {
    const campaign = campaigns.data.find(c => c.id === id);
    if (!campaign) return;
    const updated = { ...campaign, deletedAt: new Date().toISOString() };
    campaigns.setData(prev => prev.map(c => c.id === id ? updated : c));
    try {
      await dataService.campaigns.save(updated);
    } catch (e) {
      campaigns.setData(prev => prev.map(c => c.id === id ? campaign : c));
      console.error("Failed to delete campaign:", e);
    }
  }, [campaigns.data]);

  const activeCampaigns = useMemo(() => campaigns.data.filter(c => !c.deletedAt), [campaigns.data]);

  /**
   * Reload campaigns from Firestore, and RETURN the fresh list.
   *
   * The subscription above covers the ambient case; this is the explicit one,
   * for the moment right after a callable has written the record on the
   * server. The caller needs the new document in its hand — an approve whose
   * response was lost has to look at what the server actually left before it
   * writes anything — and waiting for a snapshot to arrive is not the same as
   * having read it.
   */
  const reloadCampaigns = useCallback(async () => {
    try {
      const ensureId = (doc) => doc.id ? doc : { ...doc, id: doc._docId };
      const fresh = (await dataService.campaigns.loadAll()).map(ensureId);
      campaigns.setData(fresh);
      return fresh;
    } catch (e) {
      console.error("Failed to reload campaigns:", e);
      return null;
    }
  }, []);

  /* ═══ AI CONVERSATIONS CRUD (load-only, no realtime listener) ═══ */
  const saveConversation = useCallback(async (conv) => {
    const toSave = { ...conv, updatedAt: new Date().toISOString() };
    const snapshot = aiConversations.data;
    aiConversations.setData(prev => {
      const idx = prev.findIndex(c => c.id === toSave.id);
      return idx >= 0 ? prev.map(c => c.id === toSave.id ? toSave : c) : [...prev, toSave];
    });
    try {
      await dataService.aiConversations.save(toSave);
    } catch (e) {
      aiConversations.setData(snapshot);
      notify.error("Failed to save conversation. Please try again.");
    }
  }, [aiConversations.data]);

  const removeConversation = useCallback(async (convId) => {
    const snapshot = aiConversations.data;
    aiConversations.setData(prev => prev.filter(c => c.id !== convId));
    try {
      await dataService.aiConversations.delete(convId);
    } catch (e) {
      aiConversations.setData(snapshot);
      notify.error("Failed to remove conversation. Please try again.");
    }
  }, [aiConversations.data]);

  /* ═══ TASKS CRUD ═══ */
  const saveTask = useCallback(async (task) => {
    const toSave = { ...task, updatedAt: new Date().toISOString() };
    const snapshot = tasks.data;
    tasks.setData(prev => {
      const idx = prev.findIndex(t => t.id === toSave.id);
      return idx >= 0 ? prev.map(t => t.id === toSave.id ? toSave : t) : [...prev, toSave];
    });
    try {
      await dataService.tasks.save(toSave);
    } catch (e) {
      tasks.setData(snapshot);
      notify.error("Failed to save task. Please try again.");
    }
  }, [tasks.data]);

  const removeTask = useCallback(async (taskId) => {
    const snapshot = tasks.data;
    tasks.setData(prev => prev.filter(t => t.id !== taskId));
    try {
      await dataService.tasks.delete(taskId);
    } catch (e) {
      tasks.setData(snapshot);
      notify.error("Failed to remove task. Please try again.");
    }
  }, [tasks.data]);

  const completeTask = useCallback(async (taskId) => {
    const task = tasks.data.find(t => t.id === taskId);
    if (!task) return;
    const updated = { ...task, status: "completed", completedAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
    tasks.setData(prev => prev.map(t => t.id === taskId ? updated : t));
    try {
      await dataService.tasks.save(updated);
    } catch (e) {
      tasks.setData(prev => prev.map(t => t.id === taskId ? task : t));
      notify.error("Failed to complete task. Please try again.");
    }
  }, [tasks.data]);

  const reopenTask = useCallback(async (taskId) => {
    const task = tasks.data.find(t => t.id === taskId);
    if (!task) return;
    const updated = { ...task, status: "open", completedAt: null, updatedAt: new Date().toISOString() };
    tasks.setData(prev => prev.map(t => t.id === taskId ? updated : t));
    try {
      await dataService.tasks.save(updated);
    } catch (e) {
      tasks.setData(prev => prev.map(t => t.id === taskId ? task : t));
      notify.error("Failed to reopen task. Please try again.");
    }
  }, [tasks.data]);

  /* ═══ CONTEXT VALUE ═══ */
  const value = {
    // Loading
    loaded,
    
    // Quotes
    savedQuotes: activeQuotes,
    trashedQuotes,
    draftQuotes,
    setSavedQuotes: quotes.setData,
    quotesLoading: quotes.loading,
    quotesError: quotes.error,
    deleteQuote,
    restoreQuote,
    permanentDeleteQuote,
    updateQuoteStatus,
    updateQuoteField,
    markFollowedUp,
    snoozeFollowUp,
    snoozeBizDays,
    snoozeToDate,
    loadQuote,
    duplicateQuote,
    amendQuote,
    restoreQuoteVersion,
    createChangeOrderQuote,
    startQuoteFromProspect,
    newQuote,
    saveQuote,
    saveDraft,
    loadDraft,
    deleteDraft,
    discardEdits,
    syncJobRevenueFromQuote,
    forceSyncJobRevenueFromQuote,

    // Quote filters
    filterStatus, setFilterStatus,
    filterClient, setFilterClient,
    searchTerm, setSearchTerm,

    // Builder (current quote)
    q, setQ, getQ,
    highlightMissing, setHighlightMissing,
    // The lock ({ kind, quoteId, label } or null), unsaved-edits checks and the writers
    // the builder uses for changes that arrive on their own (lib/quotePersistence.js).
    quoteBusy, unsavedEditsNotice, isQuoteDirty, quoteHadRecord, updateQuote, normalizeOpenQuote,
    setBuilderSaveBlocker, onBuilderReplaceStart,
    
    // Jobs
    jobs: activeJobs,
    trashedJobs,
    setJobs: setJobsData,
    jobsLoading: jobs.loading,
    jobsError: jobs.error,
    updateJob,
    updateJobStatus,
    toggleJobMilestone,
    deleteJob,
    restoreJob,
    permanentDeleteJob,
    backfillCalendar,
    reconcileJobCalendar,
    bookJob,
    unbookJob,
    unlinkQuote,
    saveJobDirect,
    
    // Contacts
    contacts: contacts.data,
    setContacts: contacts.setData,
    contactsLoading: contacts.loading,
    saveContact,
    removeContact,

    // Clients (v2 data model)
    clients: clients.data,
    setClients: clients.setData,
    clientsLoading: clients.loading,
    saveClient,
    removeClient,
    
    // Segments (CRM v3 slice 4) — saved audiences, resolved live
    segments: segments.data,
    segmentsLoading: segments.loading,
    saveSegment,
    removeSegment,

    // Billing
    billingQuoteId, setBillingQuoteId,
    billingNotes, setBillingNotes,
    
    // CRM
    crmImportStatus,

    // Prospects (assessments)
    assessments: activeAssessments,
    trashedAssessments,
    updateAssessmentField,
    deleteAssessment,
    restoreAssessment,
    permanentDeleteAssessment,

    // Campaigns (CRM)
    campaigns: activeCampaigns,
    campaignsLoading: campaigns.loading,
    saveCampaign,
    deleteCampaign,
    reloadCampaigns,

    // AI Conversations
    aiConversations: aiConversations.data,
    aiConversationsLoading: aiConversations.loading,
    saveConversation,
    removeConversation,

    // Tasks
    tasks: tasks.data,
    tasksLoading: tasks.loading,
    saveTask,
    removeTask,
    completeTask,
    reopenTask,

    // Studio settings (equipment / crew / storage / transport / crew roles)
    studioSettings, setStudioSettings,
    // The live roster with ids, and the live equipment map (holds)
    crewRoster,
    equipmentMap,

    // Holds (docs/holds/holds-plan.md, slice 2 contract): every hold record as
    // { id: quoteId, label, dates: [{ date, dayType, kinds }], equipmentIds,
    //   crew: [{ id, name }], fingerprint, updatedAt } for every role, read-only,
    // and the normalized role ("admin" | "superadmin" | "crew" | "viewer")
    // this provider loaded data for.
    holds: holds.data,
    holdsLoading: holds.loading,
    role: normalizedRole,

    // Crew + client cards
    crewCards: crewCards.data,
    saveCrewCard,
    applyCrewCardsSnapshot,
    applyJobsSnapshot,
    clientCards: clientCards.data,
    saveClientCard,
    flushJobSave,
  };

  return (
    <DataContext.Provider value={value}>
      {children}
      {unsavedPrompt && <UnsavedQuoteDialog prompt={unsavedPrompt} lock={quoteBusy} />}
    </DataContext.Provider>
  );
}

export function useData() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error("useData must be used within DataProvider");
  return ctx;
}

// Same context, but null outside a provider. For leaf components that are
// also rendered bare in SSR tests or fixtures (CrewFilesTab, CrewCardBadge).
export function useDataOptional() {
  return useContext(DataContext) || null;
}

export function DataContextTestProvider({ value, children }) {
  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}
